/**
 * Entity-level DXF parsing — per-entity-type group-code readers,
 * parseSupportedEntity, splitEntities, and all helper functions.
 *
 * Extracted from parser.ts — pure structural refactor.
 */

import {
  type DxfArcEntity,
  type DxfArrayRectEntity,
  type DxfAttdefEntity,
  type DxfAttribEntity,
  type DxfAttribFlagBits,
  type DxfCircleEntity,
  type DxfCommonEntityProps,
  type DxfDimensionEntity,
  type DxfEllipseEntity,
  type DxfSplineEntity,
  type DxfGroup,
  type DxfHatchBoundaryPath,
  type DxfHatchEntity,
  type DxfHatchGradient,
  type DxfHatchPatternLine,
  type DxfHatchSeedPoint,
  type DxfInsertEntity,
  type DxfLineEntity,
  type DxfLwPolylineEntity,
  type DxfLwPolylineVertex,
  type DxfMLeaderEntity,
  type DxfMLeaderLeaderLine,
  type DxfMLeaderLeaderNode,
  type DxfMLeaderLeaderRoot,
  type DxfMTextEntity,
  type DxfPoint3D,
  type DxfPointEntity,
  type DxfRevcloudEntity,
  type DxfWipeoutEntity,
  type DxfSeqendEntity,
  type DxfSupportedEntity,
  type DxfSupportedEntityType,
  type DxfTextEntity,
  type DxfViewportEntity,
  type DxfPolylineEntity,
  type DxfVertexEntity,
  type DxfAcadTableEntity,
  type DxfOle2FrameEntity,
  type DxfXDataEntry,
  isSupportedEntityType,
  normalizeEntityType,
  point3D
} from "./types";

import { StringPool } from "./stringPool";
// T9 PR-3 — symmetric `\U+XXXX` decode (inverse of the writer's legacy-codepage
// escaping) so escaped string content / symbol names re-import as the real chars.
import { decodeDxfUnicode, decodeXdataGroupValue } from "./dxfUnicodeEscape";

import { advanceMLeaderContextDepth } from "./mleaderContextDepth";

import {
  MLeaderContextBuilder,
  type MLeaderBuilderResult,
} from "./mleaderContextBuilder";

import {
  HATCH_BOUNDARY_READERS,
  finalizeHatchCurrentPath,
  readHatchBoundarySourceHandle,
  readHatchPath92,
  type HatchCurrentPath,
} from "./entityParser.hatchBoundary";

// R17 A12A-DXF-ENTITY-PARSER-REGISTRY-SEAM — the low-level group-value
// parsing primitives (previously private to this file) now live in
// entityParser.groupValueHelpers.ts, a dependency-free leaf both this file
// and entityParser.lineFamily.ts import from. Moving them out (instead of
// duplicating them a second time in entityParser.lineFamily.ts) is the
// single domain-truth fix for the jscpd clone the duplicate would
// otherwise introduce against entityParser.hatchBoundary.ts's structurally
// identical (pre-existing, out-of-scope) `num`/`int` helpers.
import {
  type MutablePoint,
  parseNumberGroup,
  setPointComponent,
} from "./entityParser.groupValueHelpers";

// LINE_GROUP_READERS (the representative extracted family) now lives in
// entityParser.lineFamily.ts and is imported back to fill the untouched
// `LINE` slot of ENTITY_GROUP_READERS below. See that module's docstring
// for why the import is one-directional (no entityParser.ts symbol flows
// the other way).
import { LINE_GROUP_READERS } from "./entityParser.lineFamily";

// Legacy heavy-POLYLINE family + ACAD_TABLE / OLE2FRAME readers live in their
// own seam modules (same one-directional convention as entityParser.lineFamily).
import {
  POLYLINE_GROUP_READERS,
  VERTEX_GROUP_READERS,
  VERTEX_FACE_INDEX_CODES,
  VERTEX_FACE_INDEX_KEY_PREFIX,
} from "./entityParser.polylineFamily";
import {
  ACAD_TABLE_GROUP_READERS,
  OLE2FRAME_GROUP_READERS,
  feedAcadTableBodyGate,
} from "./entityParser.tableOle";


/**
 * ## Function dependency map
 *
 * Public API:
 *   parseSupportedEntity(groups)
 *     -> createSupportedDraft(type)  [allocates MLeaderContextBuilder for MLEADER]
 *     -> COMMON_GROUP_READERS + ENTITY_GROUP_READERS[type] (dispatch tables)
 *         each reader calls: parseIntegerGroup / parseNumberGroup /
 *                            readPoint -> getOrCreatePoint + setPointComponent /
 *                            readRequiredNumber / flushLwVertex
 *         [MLEADER only] draft.mleaderBuilder.feed(group) per group (during walk)
 *     -> finalizeSupportedEntity(draft)
 *         -> readOptionalPoint / readOptionalPoint2D / readRequiredNumber
 *         -> draft.mleaderBuilder.finalize() (one CONTEXT_DATA descent →
 *            contextData + leaderRoots + annotativeContexts + blockAttrRefs)
 *         -> resolveMLeaderLeaderRoots(builderResult, flatLines, points)
 *     -> isXDataGroupCode (filters xdata groups)
 *
 *   splitEntities(groups) -> DxfGroup[][] (split 0-code boundaries)
 *   parseEntitiesSection(groups) -> DxfEntity[] (split + parseSupportedEntity each)
 *
 * Finalization sub-routines:
 *   resolveMLeaderLeaderRoots(builderResult, flatLines, points)
 *     -> builder hierarchy, or flat-vertex single-root fallback (marker-less)
 *
 * Group reader dispatch tables:
 *   COMMON_GROUP_READERS (codes 5, 6, 8, 39, 48, 60, 62, 67, etc.)
 *   LINE_GROUP_READERS (./entityParser.lineFamily.ts), CIRCLE_GROUP_READERS,
 *   ARC_GROUP_READERS, LWPOLYLINE_GROUP_READERS, TEXT_GROUP_READERS,
 *   MTEXT_GROUP_READERS, DIMENSION_GROUP_READERS, INSERT_GROUP_READERS,
 *   HATCH_GROUP_READERS, VIEWPORT_GROUP_READERS, MLEADER_GROUP_READERS,
 *   ARRAYRECT_GROUP_READERS, REVCLOUD_GROUP_READERS
 */

type SupportedEntityDraft = {
  type: DxfSupportedEntityType;
  common: DxfCommonEntityProps;
  subclassMarkers: string[];
  extraGroups: DxfGroup[];
  points: Record<string, MutablePoint>;
  numbers: Record<string, number | undefined>;
  strings: Record<string, string | undefined>;
  /** For LWPOLYLINE / REVCLOUD vertices */
  lwVertices: DxfLwPolylineVertex[];
  /** Current vertex being built for LWPOLYLINE */
  currentVertex: Partial<DxfLwPolylineVertex> | null;
  /**
   * For WIPEOUT clip-boundary vertices. AutoCAD encodes them as
   * repeating group-14 (x) / group-24 (y) pairs after the
   * vertex-count (group 91). We accumulate (x, y) here as the parser
   * walks the group stream and finalize into the WIPEOUT entity at
   * the end. Initialised lazily so non-WIPEOUT drafts pay no cost.
   */
  wipeoutVertices: { x: number; y: number }[];
  /** In-flight WIPEOUT vertex partial — group 14 sets x, group 24 sets y. */
  wipeoutCurrentVertex: { x?: number; y?: number } | null;
  /** For MTEXT multi-group contents (group 3 + group 1) */
  mtextParts: string[];
  /** For HATCH boundary paths */
  hatchBoundaryPaths: DxfHatchBoundaryPath[];
  hatchCurrentPath: HatchCurrentPath | null;
  hatchSeedPoints: DxfHatchSeedPoint[];
  /**
   * HATCH boundary-section state. `inHatchBoundary` is true between group 91
   * (path count) and group 75 (hatch style) so the shared codes
   * 10/20/11/21/40/50/51/72/73/93/42 disambiguate to boundary geometry rather
   * than pattern-definition / seed values. `inHatchSeed` is true after group
   * 98 (seed-point count) so a trailing 10/20 is a seed point. Both false →
   * the leading 10/20/30 elevation point is ignored (NOT minted as a seed).
   * See entityParser.hatchBoundary.ts.
   */
  inHatchBoundary: boolean;
  inHatchSeed: boolean;
  /**
   * FIX 1 — declared boundary-path count (group 91) + count of finalized paths.
   * The boundary section closes once `hatchBoundaryFinalizedCount` reaches
   * `hatchBoundaryPathTarget`, so a hatch whose closing group 75 is omitted
   * (default hatchStyle 0 export) still closes the section before the following
   * pattern-scale 41. See entityParser.hatchBoundary.ts.
   */
  hatchBoundaryPathTarget: number | undefined;
  hatchBoundaryFinalizedCount: number;
  /**
   * FIX A — true once the declared boundary-path count is finalized (even via a
   * terminator 97). Lets a trailing lone 30 be read as the HATCH elevation
   * instead of a boundary point Z. See entityParser.hatchBoundary.ts.
   */
  hatchBoundaryPathsComplete: boolean;
  /**
   * FIX A — true when a boundary point x/y (10/20 or 11/21) was just read and
   * its Z (30/31) is expected next, so that Z is consumed as a boundary point Z
   * rather than mistaken for the HATCH elevation. Reset once the Z arrives.
   */
  hatchBoundaryAwaitingZ: boolean;
  /**
   * HATCH gradient-fill accumulator (group 450..470). Lazily created on
   * the first gradient group so non-gradient hatches keep `gradient`
   * undefined and stay byte-identical. Color stops (463/63/421) are
   * appended to `colors` — a new 463 starts a new stop; the following 63
   * and 421 attach to that last stop.
   */
  /**
   * S-HATCH-CUSTOM — custom pattern-definition lines (group 78 + per-line
   * 53/43/44/45/46/79/49). Accumulated as the parser walks the pattern-data
   * block (AFTER the boundary closes, so these codes never collide with
   * boundary geometry). A group 53 OPENS a new line; the subsequent 43/44
   * (base), 45/46 (offset) and repeated 49 (dash lengths, count = 79) attach
   * to it. Empty for solid fills / predefined patterns. `hatchPatternLineTarget`
   * holds the declared 78 count (informational — we accumulate per-53 so a
   * miscounted 78 cannot drop a line). See model.ts hatchPatternLineSchema.
   */
  hatchPatternLines: DxfHatchPatternLine[];
  hatchCurrentPatternLine: DxfHatchPatternLine | null;
  hatchPatternLineTarget: number | undefined;
  // BUG 2 — true when `hatchCurrentPatternLine` was minted as a placeholder by
  // ensureHatchPatternLine (an orphan 43/44/45/46/49/79 arrived BEFORE its 53)
  // and has not yet received its 53 angle. On 53 the angle is set in-place on
  // the placeholder instead of pushing a SECOND line; cleared once 53 assigns
  // the angle (and whenever a fresh, angle-bearing line is opened).
  hatchCurrentPatternLineNeedsAngle: boolean;
  hatchGradient: DxfHatchGradient | null;
  // True once a gradient flag 450=1 is seen — gates whether the 450..470 block
  // populates the `gradient` model field or is preserved as passthrough. A solid
  // hatch's 450=0 block keeps this false so `gradient` stays undefined and the
  // hatch is not misclassified as a gradient (Codex #208 iter-3).
  hatchGradientActive: boolean;
  // True only while the parser is INSIDE a gradient color-stop context (between
  // a 463 and the closing 470). Gates 63/421: after 470 the gradient is sealed,
  // so a 63/421 appended later (e.g. extraGroups re-serialized after 470) is a
  // passthrough, NOT an overwrite of the last stop (Codex #208 iter-2).
  hatchGradientStopOpen: boolean;
  /** For MLEADER leader lines */
  mleaderLines: DxfMLeaderLeaderLine[];
  mleaderCurrentVertices: DxfPoint3D[];
  /**
   * SPLINE accumulators. AutoCAD interleaves knot values (group 40),
   * weights (group 41), control points (10/20/30) and fit points
   * (11/21/31). The parser pushes one element per occurrence; the
   * finalisation step assembles them into typed arrays.
   */
  splineKnots: number[];
  splineWeights: number[];
  splineControlPoints: DxfPoint3D[];
  splineFitPoints: DxfPoint3D[];
  splineCurrentControlPoint: { x?: number; y?: number; z?: number } | null;
  splineCurrentFitPoint: { x?: number; y?: number; z?: number } | null;
  /**
   * MLEADER CONTEXT_DATA nested-tree builder. Fed every MLEADER group during
   * the dispatch walk; at finalize it yields the typed contextData tree, the
   * proper leaderRoots hierarchy, the per-scale annotative snapshots and the
   * trailing block-attribute linkage tuples in ONE descent (collapsing the
   * former buildMLeaderRoots / extractAnnotativeContexts /
   * extractBlockAttributeReferences passes). Only allocated for MLEADER
   * entities; `null` otherwise.
   */
  mleaderBuilder: MLeaderContextBuilder | null;
  /**
   * True while the MLEADER parser is INSIDE a CONTEXT_DATA block (between
   * the opening `300 CONTEXT_DATA{` and its matching `303 }` close,
   * including nested LEADER{ / LEADER_LINE{ / `301 {` sub-blocks). An
   * MLEADER carries MULTIPLE group-340 handles: a TOP-LEVEL one
   * (LeaderLineType handle, outside CONTEXT_DATA) AND the text-style
   * handle inside the CONTEXT_DATA / MTextData block (ezdxf
   * `MTextData.style_handle`). The group-340 reader must only capture
   * `textStyleHandle` when this flag is set; the top-level 340 keeps its
   * original handling (preserved verbatim via extraGroups) so the
   * LeaderLineType reference is never stolen. See the `isMLeader`
   * context-tracking block in the main dispatch loop.
   */
  mleaderInContextData: boolean;
  /** CONTEXT_DATA nesting depth, mirroring the MLeaderContextBuilder descent. */
  mleaderContextDepth: number;
  /**
   * 1-based ordinal of the CONTEXT_DATA block currently (or last) entered —
   * incremented on every top-level (depth 0→1) context open. An annotative
   * MLEADER carries MULTIPLE CONTEXT_DATA blocks: the FIRST (primary) one is
   * the entity's current representation; every later block is a per-scale
   * SECONDARY copy the builder keeps fully typed (`secondaryContexts`). The
   * in-context flat readers (304 textContents / 340 textStyleHandle /
   * 12/22/32 textLocation) project PRIMARY-context values onto the flat
   * geometry fields the bridge consumes — they must NOT fire inside a
   * SECONDARY block: a secondary header-level 340 captured onto
   * `textStyleHandle` made the primary writer re-emit it as a legacy style
   * stamp in the PRIMARY block while the secondary raw channel re-emitted the
   * original — a leaked AND duplicated 340 (#294 Codex P2, secondary-raw-340
   * dup). Gate: flat readers fire only while `mleaderContextBlockOrdinal===1`.
   */
  mleaderContextBlockOrdinal: number;
  /**
   * #432 r9 E1 — true once the `100 AcDbMLeader` subclass marker has been seen,
   * i.e. we are in the AcDbMLeader BODY. Disambiguates the trailing block-attribute
   * tuple `330 <attdefHandle>` (block-attr, body) from the ENTITY-OWNER `330`
   * (pre-subclass, common region): only a body `330` is consumed into
   * `blockAttributeReferences` — the owner one still reaches `common.ownerHandle`.
   */
  mleaderBodyStarted: boolean;
  /**
   * #432 r9 E1 — true while INSIDE a depth-0 block-attribute tuple (opened by a
   * body `330`, closed by its `302`). Mirrors the context builder's `feedBody`
   * state so the dispatch consumes exactly the `330/177/44/302` the builder
   * captured, while a STRAY `177/44/302` with no open tuple still round-trips
   * verbatim via extraGroups (never drop — ПРИОРИТЕТ #1).
   */
  mleaderBlockAttrOpen: boolean;
  /**
   * #432 r11 (@codex) — a body `330` opener held pending until the NEXT group
   * decides its fate: a following `177/44/302` makes it a REAL tuple (consumed —
   * the builder captured it); anything else (another `330`, a non-tuple code, or
   * end-of-body) makes it a BARE/malformed opener → parked verbatim in extraGroups
   * so it round-trips instead of being fabricated into a block-attribute. `null`
   * when no opener is pending.
   */
  mleaderPending330: string | null;
  /**
   * True when the MLEADER's CONTEXT_DATA is wrapped in a `101 "Embedded Object"`
   * / `100 "AcDbMLeaderObjectContextData"` record (vs the common top-level form).
   * The builder consumes the wrapped 300…301 into the typed tree the same way;
   * this flag lets the writer RE-WRAP the re-emitted CONTEXT_DATA in the 101/100
   * markers instead of leaving a dangling 101/100 (parked verbatim) at the entity
   * tail while CONTEXT_DATA moved to top level (Codex #268).
   */
  mleaderContextEmbedded: boolean;
  /**
   * S-MTEXT-COLUMN-HEIGHTS — per-column heights decoded from the MTEXT
   * `101 AcDbMText` embedded-object block (R2018+). The embedded block's
   * layout (ezdxf MTextColumns.export_embedded_object) is:
   *   101 "Embedded Object" / 70 / 10 (text dir) / 11 (insert) / 40 (ref
   *   width) / 41 (defined height) / 42 (total width) / 43 (total height) /
   *   71 (column type) / 72 (column COUNT) / 44 (column width) / 45 (gutter) /
   *   73 (auto height) / 74 (reversed flow) / then REPEATED 46 (per-column
   *   heights, one per column).
   * The per-column 46s are the ONLY 46s in the embedded block and always
   * come AFTER the column-count group 72. We capture them here for a typed
   * read-only projection into geometry.columnHeights WITHOUT removing them
   * from extraGroups — the writer re-emits the whole embedded block verbatim
   * from extraGroups (single source of truth, no double-emit). A TOP-LEVEL
   * body 46 (scalar defined column height, before the `101` marker) must NOT
   * enter this array (Codex PR #254 guard) — `mtextInEmbeddedColumns` gates
   * that: it only turns true after the embedded `72` count is seen.
   */
  mtextEmbeddedColumnHeights: number[];
  /**
   * True once, inside the MTEXT `101 AcDbMText` embedded object, the
   * column-count group 72 has been observed. Gates capture of the subsequent
   * repeated 46s as per-column heights (so the embedded block's leading 46?
   * never appears — there is no leading 46 — and a top-level body 46 outside
   * the embedded object is never captured).
   */
  mtextInEmbeddedColumns: boolean;
  /**
   * VIEWPORT per-VP frozen-layer soft-pointer handles (DXF group 331, repeating)
   * — S-VIEWPORT-OVERRIDE-331. Each 331 is the handle of a LAYER record frozen
   * in this viewport. Accumulated as the parser walks the body; pulled out of
   * extraGroups into the typed `frozenLayerHandles[]` at finalize.
   */
  viewportFrozenLayerHandles: string[];
  /**
   * VIEWPORT per-VP layer PROPERTY-override soft-pointer references (DXF groups
   * 335 / 343 / 344 / 91) — S-VIEWPORT-OVERRIDE-331 companion. Stored as
   * {code, handle} pairs so the exact group code per entry round-trips
   * byte-faithfully (NOT collapsed to a flat handle bag).
   */
  viewportLayerOverrideHandles: { code: number; handle: string }[];
  /**
   * VIEWPORT clipping-boundary soft-pointer handle (DXF group 340) —
   * S-VIEWPORT-CLIP-HANDLE (EPIC-VIEWPORT-CLIP slice 1). Hard-pointer to the
   * graphical entity in paperspace (LWPOLYLINE / CIRCLE / ELLIPSE / closed
   * SPLINE) that defines this viewport's non-rectangular clip region. A SINGLE
   * value (not repeating), captured verbatim and pulled out of extraGroups into
   * the typed `clippingBoundaryHandle` at finalize. Source of truth: ezdxf 1.4.3
   * entities/viewport.py acdb_viewport `"clipping_boundary_handle": DXFAttr(340,
   * default="0", optional=True)` + Autodesk DXF reference AcDbViewport ("Hard-
   * pointer ID/handle to clipping boundary"). undefined until a body 340 is seen.
   */
  viewportClippingBoundaryHandle?: string;
  /**
   * ACAD_TABLE / OLE2FRAME binary payload chunks (group 310), hex verbatim
   * and in file order. NEVER decoded — the ACAD_TABLE blob is AutoCAD's
   * regenerable cached graphics (we render the referenced `*T<N>` block
   * instead), and the OLE2FRAME blob is an OLE compound document whose EMF
   * stream needs a rasteriser this project does not have.
   */
  binaryChunks: string[];
  /**
   * ACAD_TABLE — the AcDbTable body verbatim from the first group after the
   * documented header (…96) to the end of the subclass: the 141/142 sizing
   * groups and every per-cell record. This is the table's content model,
   * retained rather than decoded because cell editing is out of scope
   * (see `DxfAcadTableEntity`'s type doc). Single write source for the
   * writer.
   */
  tableContentGroups: DxfGroup[];
  /** ACAD_TABLE — 141 row heights, READ-ONLY projection over the groups above. */
  tableRowHeights: number[];
  /** ACAD_TABLE — 142 column widths, READ-ONLY projection over the same. */
  tableColumnWidths: number[];
  /** ACAD_TABLE — true once the `100 AcDbTable` subclass marker was seen. */
  tableBodyStarted: boolean;
  /** ACAD_TABLE — true once the documented header ended and content began. */
  tableContentOpen: boolean;
  /** ACAD_TABLE — header codes already consumed; a repeat opens the content. */
  tableHeaderCodesSeen: Set<number>;
  /** For XDATA (extended data) blocks */
  xdata: DxfXDataEntry[];
  /**
   * Optional per-parse-call string pool for interning layer, linetype,
   * styleName, blockName, patternName and other recurring string fields.
   * A production DXF with thousands of entities only has ~50 unique
   * values for these fields; pooling reduces heap pressure 20-30%.
   * Undefined in legacy callers (e.g. direct `parseSupportedEntity`
   * unit tests) so pooling is strictly opt-in.
   */
  stringPool: StringPool | undefined;
};

type GroupReader = (draft: SupportedEntityDraft, value: string) => void;

function parseIntegerGroup(code: number, value: string): number {
  const parsed = Number.parseInt(value.trim(), 10);

  if (!Number.isInteger(parsed)) {
    throw new Error(`Group ${code} expected an integer, received "${value}"`);
  }

  return parsed;
}

function readPoint(
  points: Record<string, MutablePoint>,
  key: string,
  label: string
): DxfPoint3D {
  const point = points[key];

  if (point?.x === undefined || point.y === undefined) {
    throw new Error(`${label} requires X and Y coordinates`);
  }

  return {
    x: point.x,
    y: point.y,
    z: point.z ?? 0
  };
}

function readRequiredNumber(
  numbers: Record<string, number | undefined>,
  key: string,
  label: string
): number {
  const value = numbers[key];

  if (value === undefined) {
    throw new Error(`${label} is required`);
  }

  return value;
}

function createSupportedDraft(
  type: DxfSupportedEntityType,
  stringPool: StringPool | undefined = undefined,
): SupportedEntityDraft {
  return {
    type,
    common: {},
    subclassMarkers: [],
    extraGroups: [],
    points: {},
    numbers: {},
    strings: {},
    lwVertices: [],
    currentVertex: null,
    wipeoutVertices: [],
    wipeoutCurrentVertex: null,
    mtextParts: [],
    hatchBoundaryPaths: [],
    hatchCurrentPath: null,
    hatchSeedPoints: [],
    inHatchBoundary: false,
    inHatchSeed: false,
    hatchBoundaryPathTarget: undefined,
    hatchBoundaryFinalizedCount: 0,
    hatchBoundaryPathsComplete: false,
    hatchBoundaryAwaitingZ: false,
    hatchPatternLines: [],
    hatchCurrentPatternLine: null,
    hatchPatternLineTarget: undefined,
    hatchCurrentPatternLineNeedsAngle: false,
    hatchGradient: null,
    hatchGradientActive: false,
    hatchGradientStopOpen: false,
    mleaderLines: [],
    mleaderCurrentVertices: [],
    mleaderBuilder: type === "MLEADER" ? new MLeaderContextBuilder() : null,
    mleaderInContextData: false,
    mleaderContextDepth: 0,
    mleaderContextBlockOrdinal: 0,
    mleaderBodyStarted: false,
    mleaderBlockAttrOpen: false,
    mleaderPending330: null,
    mleaderContextEmbedded: false,
    mtextEmbeddedColumnHeights: [],
    mtextInEmbeddedColumns: false,
    viewportFrozenLayerHandles: [],
    viewportLayerOverrideHandles: [],
    splineKnots: [],
    splineWeights: [],
    splineControlPoints: [],
    splineFitPoints: [],
    splineCurrentControlPoint: null,
    splineCurrentFitPoint: null,
    binaryChunks: [],
    tableContentGroups: [],
    tableRowHeights: [],
    tableColumnWidths: [],
    tableBodyStarted: false,
    tableContentOpen: false,
    tableHeaderCodesSeen: new Set<number>(),
    xdata: [],
    stringPool,
  };
}

/**
 * Intern a trimmed value through the draft's optional string pool.
 * When the draft has no pool attached (legacy callers / unit tests),
 * returns the trimmed string as-is so behaviour is identical.
 */
function internTrim(draft: SupportedEntityDraft, value: string): string {
  // T9 PR-3 — decode `\U+XXXX` so escaped symbol names (layer / block / style
  // refs, ATTRIB/ATTDEF tag) re-import as the real characters. Names are used
  // as handle-resolution keys, so the decode must happen here, symmetric with
  // the writer's name escaping (ezdxf escapes R2000 symbol names too).
  const trimmed = decodeDxfUnicode(value).trim();
  return draft.stringPool !== undefined ? draft.stringPool.intern(trimmed) : trimmed;
}

const COMMON_GROUP_READERS: Record<number, GroupReader> = {
  5: (draft, value) => {
    draft.common.handle = value.trim();
  },
  6: (draft, value) => {
    draft.common.linetype = internTrim(draft, value);
  },
  8: (draft, value) => {
    draft.common.layer = internTrim(draft, value);
  },
  39: (draft, value) => {
    draft.common.thickness = parseNumberGroup(39, value);
  },
  48: (draft, value) => {
    draft.common.linetypeScale = parseNumberGroup(48, value);
  },
  60: (draft, value) => {
    draft.common.visibility = parseIntegerGroup(60, value);
  },
  62: (draft, value) => {
    draft.common.colorNumber = parseIntegerGroup(62, value);
  },
  67: (draft, value) => {
    draft.common.space = parseIntegerGroup(67, value) === 1 ? "paper" : "model";
  },
  100: (draft, value) => {
    draft.subclassMarkers.push(value.trim());
  },
  210: (draft, value) => {
    setPointComponent(draft.points, "__extrusion__", "x", 210, value);
  },
  220: (draft, value) => {
    setPointComponent(draft.points, "__extrusion__", "y", 220, value);
  },
  230: (draft, value) => {
    setPointComponent(draft.points, "__extrusion__", "z", 230, value);
  },
  284: (draft, value) => {
    draft.common.shadowMode = parseIntegerGroup(284, value);
  },
  330: (draft, value) => {
    draft.common.ownerHandle = value.trim();
  },
  347: (draft, value) => {
    draft.common.materialHandle = value.trim();
  },
  370: (draft, value) => {
    draft.common.lineweight = parseIntegerGroup(370, value);
  },
  390: (draft, value) => {
    draft.common.plotStyleHandle = value.trim();
  },
  410: (draft, value) => {
    draft.common.layoutName = decodeDxfUnicode(value.trim()); // T9 PR-3 — layout name decode
  },
  420: (draft, value) => {
    draft.common.trueColor = parseIntegerGroup(420, value);
  },
  430: (draft, value) => {
    draft.common.colorName = decodeDxfUnicode(value.trim()); // T9 PR-3 — colour book/name decode
  },
  440: (draft, value) => {
    draft.common.transparency = parseIntegerGroup(440, value);
  }
};

// AcDbPoint group codes: 10/20/30 — location, 50 — UCS X-axis angle
// (DEGREES — group-code-value-types spec + ezdxf; stored verbatim) used by
// PDMODE shapes. Thickness (39) and extrusion (210/220/230) flow through the
// common reader.
const POINT_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => {
    setPointComponent(draft.points, "location", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "location", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "location", "z", 30, value);
  },
  50: (draft, value) => {
    draft.numbers.rotation = parseNumberGroup(50, value);
  }
};

const CIRCLE_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => {
    setPointComponent(draft.points, "center", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "center", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "center", "z", 30, value);
  },
  40: (draft, value) => {
    draft.numbers.radius = parseNumberGroup(40, value);
  }
};

const ARC_GROUP_READERS: Record<number, GroupReader> = {
  ...CIRCLE_GROUP_READERS,
  50: (draft, value) => {
    draft.numbers.startAngle = parseNumberGroup(50, value);
  },
  51: (draft, value) => {
    draft.numbers.endAngle = parseNumberGroup(51, value);
  }
};

/* ── ELLIPSE ───────────────────────────────────────── */
// AcDbEllipse group codes:
//   10/20/30 — center point
//   11/21/31 — major-axis endpoint (vector relative to center)
//   210/220/230 — extrusion direction (OCS); routed to common.extrusion
//                 by the entity reader's __extrusion__ point key.
//   40 — ratio (minor/major), positive
//   41 — start parameter (radians)
//   42 — end parameter (radians)
const ELLIPSE_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => { setPointComponent(draft.points, "center", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "center", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "center", "z", 30, value); },
  11: (draft, value) => { setPointComponent(draft.points, "majorAxis", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "majorAxis", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "majorAxis", "z", 31, value); },
  40: (draft, value) => { draft.numbers.ratio = parseNumberGroup(40, value); },
  41: (draft, value) => { draft.numbers.startParam = parseNumberGroup(41, value); },
  42: (draft, value) => { draft.numbers.endParam = parseNumberGroup(42, value); },
};

/* ── SPLINE ────────────────────────────────────────── */
// AcDbSpline group codes:
//   70   flags bitfield
//   71   degree
//   72   knot count
//   73   control-point count
//   74   fit-point count
//   42   knot tolerance
//   43   control-point tolerance
//   44   fit tolerance
//   12/22/32  start tangent
//   13/23/33  end tangent
//   40   (multiple) knot values
//   41   (multiple) control-point weights
//   10/20/30 (multiple) control points
//   11/21/31 (multiple) fit points
//
// Control points and fit points appear interleaved with their counts —
// the writer always emits 10 first, then 20, then optionally 30 per
// control point. We flush a control-point partial whenever we see a
// new 10 (start a fresh point) and assemble final arrays at finalisation
// time.
function flushSplineControlPoint(draft: SupportedEntityDraft): void {
  const cp = draft.splineCurrentControlPoint;
  if (cp && cp.x !== undefined && cp.y !== undefined) {
    draft.splineControlPoints.push({ x: cp.x, y: cp.y, z: cp.z ?? 0 });
  }
  draft.splineCurrentControlPoint = null;
}

function flushSplineFitPoint(draft: SupportedEntityDraft): void {
  const fp = draft.splineCurrentFitPoint;
  if (fp && fp.x !== undefined && fp.y !== undefined) {
    draft.splineFitPoints.push({ x: fp.x, y: fp.y, z: fp.z ?? 0 });
  }
  draft.splineCurrentFitPoint = null;
}

const SPLINE_GROUP_READERS: Record<number, GroupReader> = {
  70: (draft, value) => { draft.numbers.flags = parseIntegerGroup(70, value); },
  71: (draft, value) => { draft.numbers.degree = parseIntegerGroup(71, value); },
  72: (draft, value) => { draft.numbers.knotCount = parseIntegerGroup(72, value); },
  73: (draft, value) => { draft.numbers.controlPointCount = parseIntegerGroup(73, value); },
  74: (draft, value) => { draft.numbers.fitPointCount = parseIntegerGroup(74, value); },
  42: (draft, value) => { draft.numbers.knotTolerance = parseNumberGroup(42, value); },
  43: (draft, value) => { draft.numbers.controlPointTolerance = parseNumberGroup(43, value); },
  44: (draft, value) => { draft.numbers.fitTolerance = parseNumberGroup(44, value); },
  // Start tangent (group 12/22/32) and end tangent (group 13/23/33).
  12: (draft, value) => { setPointComponent(draft.points, "splineStartTangent", "x", 12, value); },
  22: (draft, value) => { setPointComponent(draft.points, "splineStartTangent", "y", 22, value); },
  32: (draft, value) => { setPointComponent(draft.points, "splineStartTangent", "z", 32, value); },
  13: (draft, value) => { setPointComponent(draft.points, "splineEndTangent", "x", 13, value); },
  23: (draft, value) => { setPointComponent(draft.points, "splineEndTangent", "y", 23, value); },
  33: (draft, value) => { setPointComponent(draft.points, "splineEndTangent", "z", 33, value); },
  // Multiple knot values
  40: (draft, value) => { draft.splineKnots.push(parseNumberGroup(40, value)); },
  // Multiple weights
  41: (draft, value) => { draft.splineWeights.push(parseNumberGroup(41, value)); },
  // Multiple control points (10/20/30 — each new 10 starts a fresh point)
  10: (draft, value) => {
    flushSplineControlPoint(draft);
    draft.splineCurrentControlPoint = { x: parseNumberGroup(10, value) };
  },
  20: (draft, value) => {
    if (draft.splineCurrentControlPoint) {
      draft.splineCurrentControlPoint.y = parseNumberGroup(20, value);
    }
  },
  30: (draft, value) => {
    if (draft.splineCurrentControlPoint) {
      draft.splineCurrentControlPoint.z = parseNumberGroup(30, value);
    }
  },
  // Multiple fit points (11/21/31 — each new 11 starts a fresh point)
  11: (draft, value) => {
    flushSplineFitPoint(draft);
    draft.splineCurrentFitPoint = { x: parseNumberGroup(11, value) };
  },
  21: (draft, value) => {
    if (draft.splineCurrentFitPoint) {
      draft.splineCurrentFitPoint.y = parseNumberGroup(21, value);
    }
  },
  31: (draft, value) => {
    if (draft.splineCurrentFitPoint) {
      draft.splineCurrentFitPoint.z = parseNumberGroup(31, value);
    }
  },
};

/* ── LWPOLYLINE ────────────────────────────────────── */

function flushLwVertex(draft: SupportedEntityDraft): void {
  if (draft.currentVertex && draft.currentVertex.x !== undefined && draft.currentVertex.y !== undefined) {
    const v: DxfLwPolylineVertex = { x: draft.currentVertex.x, y: draft.currentVertex.y };
    if (draft.currentVertex.bulge !== undefined) v.bulge = draft.currentVertex.bulge;
    if (draft.currentVertex.startWidth !== undefined) v.startWidth = draft.currentVertex.startWidth;
    if (draft.currentVertex.endWidth !== undefined) v.endWidth = draft.currentVertex.endWidth;
    if (draft.currentVertex.vertexId !== undefined) v.vertexId = draft.currentVertex.vertexId;
    draft.lwVertices.push(v);
  }
  draft.currentVertex = null;
}

const LWPOLYLINE_GROUP_READERS: Record<number, GroupReader> = {
  90: (draft, value) => { draft.numbers.vertexCount = parseIntegerGroup(90, value); },
  70: (draft, value) => { draft.numbers.flags = parseIntegerGroup(70, value); },
  43: (draft, value) => { draft.numbers.constantWidth = parseNumberGroup(43, value); },
  38: (draft, value) => { draft.numbers.elevation = parseNumberGroup(38, value); },
  10: (draft, value) => {
    flushLwVertex(draft);
    draft.currentVertex = { x: parseNumberGroup(10, value) };
  },
  20: (draft, value) => {
    if (draft.currentVertex) {
      draft.currentVertex.y = parseNumberGroup(20, value);
    }
  },
  42: (draft, value) => {
    if (draft.currentVertex) {
      draft.currentVertex.bulge = parseNumberGroup(42, value);
    }
  },
  40: (draft, value) => {
    if (draft.currentVertex) {
      draft.currentVertex.startWidth = parseNumberGroup(40, value);
    }
  },
  41: (draft, value) => {
    if (draft.currentVertex) {
      draft.currentVertex.endWidth = parseNumberGroup(41, value);
    }
  },
  // DXF group 91 — per-vertex persistent identifier, interleaved with the
  // 10/20/42 codes inside AcDbPolyline body. See DxfLwPolylineVertex
  // docstring for semantics.
  91: (draft, value) => {
    if (draft.currentVertex) {
      draft.currentVertex.vertexId = parseIntegerGroup(91, value);
    }
  }
};

/* ── TEXT ──────────────────────────────────────────── */

const TEXT_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => { setPointComponent(draft.points, "insertion", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "insertion", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "insertion", "z", 30, value); },
  11: (draft, value) => { setPointComponent(draft.points, "alignment", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "alignment", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "alignment", "z", 31, value); },
  40: (draft, value) => { draft.numbers.height = parseNumberGroup(40, value); },
  50: (draft, value) => { draft.numbers.rotation = parseNumberGroup(50, value); },
  1:  (draft, value) => { draft.strings.contents = value; },
  7:  (draft, value) => { draft.strings.styleName = internTrim(draft, value); },
  41: (draft, value) => { draft.numbers.widthFactor = parseNumberGroup(41, value); },
  51: (draft, value) => { draft.numbers.obliqueAngle = parseNumberGroup(51, value); },
  71: (draft, value) => { draft.numbers.generationFlags = parseIntegerGroup(71, value); },
  72: (draft, value) => { draft.numbers.hJustification = parseIntegerGroup(72, value); },
  73: (draft, value) => { draft.numbers.vJustification = parseIntegerGroup(73, value); }
};

/* ── MTEXT ─────────────────────────────────────────── */

const MTEXT_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => { setPointComponent(draft.points, "insertion", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "insertion", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "insertion", "z", 30, value); },
  11: (draft, value) => { setPointComponent(draft.points, "xAxisDir", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "xAxisDir", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "xAxisDir", "z", 31, value); },
  40: (draft, value) => { draft.numbers.nominalHeight = parseNumberGroup(40, value); },
  41: (draft, value) => { draft.numbers.referenceWidth = parseNumberGroup(41, value); },
  42: (draft, value) => { draft.numbers.horizontalWidth = parseNumberGroup(42, value); },
  43: (draft, value) => { draft.numbers.verticalHeight = parseNumberGroup(43, value); },
  44: (draft, value) => { draft.numbers.lineSpacingFactor = parseNumberGroup(44, value); },
  45: (draft, value) => { draft.numbers.fillBoxScale = parseNumberGroup(45, value); },
  // NOTE: MTEXT group 46 is intentionally NOT read here. Group 46 in the
  // top-level MTEXT body (before any `101 Embedded Object` marker) is the
  // SCALAR "defined column height", NOT a per-column height. The per-column
  // heights are the repeated group 46 INSIDE the `101 AcDbMText` embedded
  // column block, after the column-height-count (group 72). This entity-body
  // reader table only ever sees top-level groups (the parser routes
  // everything after `101` straight to extraGroups — see the inEmbeddedObject
  // gate in parseSupportedEntity), so a 46 reaching here is the scalar
  // defined height. Faithful per-column-height round-trip requires modelling
  // the `101 AcDbMText` embedded-object internal layout (count group 72 +
  // repeated 46) in both the parser and the writer — deferred as
  // S-MTEXT-COLUMN-HEIGHTS (needs embedded-object support; see
  // dxf-fidelity-audit.md). Reading body-level 46 as a per-column height
  // corrupts no-column / static / dynamic-auto MTEXT (Codex PR #254 P1).
  48: (draft, value) => { draft.numbers.columnWidth = parseNumberGroup(48, value); },
  49: (draft, value) => { draft.numbers.columnGutter = parseNumberGroup(49, value); },
  71: (draft, value) => { draft.numbers.attachmentPoint = parseIntegerGroup(71, value); },
  72: (draft, value) => { draft.numbers.drawingDirection = parseIntegerGroup(72, value); },
  73: (draft, value) => { draft.numbers.lineSpacingStyle = parseIntegerGroup(73, value); },
  75: (draft, value) => { draft.numbers.columnType = parseIntegerGroup(75, value); },
  76: (draft, value) => { draft.numbers.columnCount = parseIntegerGroup(76, value); },
  78: (draft, value) => { draft.numbers.columnFlowReversed = parseIntegerGroup(78, value); },
  79: (draft, value) => { draft.numbers.columnAutoheight = parseIntegerGroup(79, value); },
  90: (draft, value) => { draft.numbers.backgroundFillSetting = parseIntegerGroup(90, value); },
  63: (draft, value) => { draft.numbers.backgroundFillColorACI = parseIntegerGroup(63, value); },
  420: (draft, value) => { draft.numbers.backgroundFillColorRGB = parseIntegerGroup(420, value); },
  441: (draft, value) => { draft.numbers.backgroundTransparency = parseIntegerGroup(441, value); },
  1:  (draft, value) => { draft.strings.contents = value; },
  3:  (draft, value) => { draft.mtextParts.push(value); },
  7:  (draft, value) => { draft.strings.styleName = internTrim(draft, value); },
  50: (draft, value) => { draft.numbers.rotation = parseNumberGroup(50, value); }
};

/* ── DIMENSION ─────────────────────────────────────── */

const DIMENSION_GROUP_READERS: Record<number, GroupReader> = {
  70: (draft, value) => { draft.numbers.dimensionType = parseIntegerGroup(70, value); },
  // Group 71 — MTEXT-style attachment point (1..9; default 5 Middle-
  // Center). Common Dimension Group Codes (Autodesk DXF Reference).
  71: (draft, value) => { draft.numbers.attachmentPoint = parseIntegerGroup(71, value); },
  10: (draft, value) => { setPointComponent(draft.points, "defPoint", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "defPoint", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "defPoint", "z", 30, value); },
  11: (draft, value) => { setPointComponent(draft.points, "textMidpoint", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "textMidpoint", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "textMidpoint", "z", 31, value); },
  1:  (draft, value) => { draft.strings.dimensionText = value; },
  2:  (draft, value) => { draft.strings.anonymousBlockName = internTrim(draft, value); },
  3:  (draft, value) => { draft.strings.styleName = internTrim(draft, value); },
  13: (draft, value) => { setPointComponent(draft.points, "ext1", "x", 13, value); },
  23: (draft, value) => { setPointComponent(draft.points, "ext1", "y", 23, value); },
  33: (draft, value) => { setPointComponent(draft.points, "ext1", "z", 33, value); },
  14: (draft, value) => { setPointComponent(draft.points, "ext2", "x", 14, value); },
  24: (draft, value) => { setPointComponent(draft.points, "ext2", "y", 24, value); },
  34: (draft, value) => { setPointComponent(draft.points, "ext2", "z", 34, value); },
  15: (draft, value) => { setPointComponent(draft.points, "thirdPoint", "x", 15, value); },
  25: (draft, value) => { setPointComponent(draft.points, "thirdPoint", "y", 25, value); },
  35: (draft, value) => { setPointComponent(draft.points, "thirdPoint", "z", 35, value); },
  16: (draft, value) => { setPointComponent(draft.points, "arcPoint", "x", 16, value); },
  26: (draft, value) => { setPointComponent(draft.points, "arcPoint", "y", 26, value); },
  36: (draft, value) => { setPointComponent(draft.points, "arcPoint", "z", 36, value); },
  40: (draft, value) => { draft.numbers.leaderLength = parseNumberGroup(40, value); },
  50: (draft, value) => { draft.numbers.linearAngle = parseNumberGroup(50, value); },
  51: (draft, value) => { draft.numbers.obliqueAngle = parseNumberGroup(51, value); },
  52: (draft, value) => { draft.numbers.textRotation = parseNumberGroup(52, value); },
  53: (draft, value) => { draft.numbers.textAngleRelativeToDimLine = parseNumberGroup(53, value); },
  42: (draft, value) => { draft.numbers.measurement = parseNumberGroup(42, value); },
  // S-DIMENSION-EXTRAS — AcDbDimension subclass codes (disambiguated vs
  // ezdxf `ezdxf.entities.Dimension` + the codes already read above).
  // Group 280 — AcDbDimension version flag (ezdxf `dxf.version`).
  280: (draft, value) => { draft.numbers.dimVersion = parseIntegerGroup(280, value); },
  // Group 72/41 — text line-spacing style + factor (ezdxf
  // `dxf.text_line_spacing_style` / `dxf.text_line_spacing_factor`). 41 is
  // free in this per-type table (42=measurement, 40=leaderLength).
  72: (draft, value) => { draft.numbers.dimLineSpacingStyle = parseIntegerGroup(72, value); },
  41: (draft, value) => { draft.numbers.dimLineSpacingFactor = parseNumberGroup(41, value); },
  // Group 12/22/32 — insertion point for Baseline/Continue clones (ezdxf
  // `Dimension.dxf.insert`). Distinct from defPoint (10) / textMidpoint (11).
  12: (draft, value) => { setPointComponent(draft.points, "dimInsert", "x", 12, value); },
  22: (draft, value) => { setPointComponent(draft.points, "dimInsert", "y", 22, value); },
  32: (draft, value) => { setPointComponent(draft.points, "dimInsert", "z", 32, value); }
};

/* ── INSERT ────────────────────────────────────────── */

const INSERT_GROUP_READERS: Record<number, GroupReader> = {
  2:  (draft, value) => { draft.strings.blockName = internTrim(draft, value); },
  10: (draft, value) => { setPointComponent(draft.points, "insertion", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "insertion", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "insertion", "z", 30, value); },
  41: (draft, value) => { draft.numbers.scaleX = parseNumberGroup(41, value); },
  42: (draft, value) => { draft.numbers.scaleY = parseNumberGroup(42, value); },
  43: (draft, value) => { draft.numbers.scaleZ = parseNumberGroup(43, value); },
  50: (draft, value) => { draft.numbers.rotation = parseNumberGroup(50, value); },
  // Group 66 — "attributes follow" flag. When present and set to 1,
  // one or more ATTRIB entities terminated by a SEQEND follow the INSERT.
  66: (draft, value) => { draft.numbers.attributesFollow = parseIntegerGroup(66, value); },
  70: (draft, value) => { draft.numbers.colCount = parseIntegerGroup(70, value); },
  71: (draft, value) => { draft.numbers.rowCount = parseIntegerGroup(71, value); },
  44: (draft, value) => { draft.numbers.colSpacing = parseNumberGroup(44, value); },
  45: (draft, value) => { draft.numbers.rowSpacing = parseNumberGroup(45, value); }
};

/* ── ATTDEF / ATTRIB (shared AcDbText + AcDbAttributeDefinition fields) ── */

// Group code readers shared between ATTDEF and ATTRIB. Both entities
// derive from AcDbText and inherit its group codes (10/11 positions,
// 40 height, 50 rotation, 7 style, 41 widthFactor, 51 oblique, 71/72/74
// text generation / justification). ATTDEF additionally stores the
// prompt (group 3) and defaultValue via group 1; ATTRIB stores only the
// actual attribute value via group 1.
const ATTDEF_GROUP_READERS: Record<number, GroupReader> = {
  1:  (draft, value) => { draft.strings.defaultValue = value; },
  2:  (draft, value) => { draft.strings.tag = internTrim(draft, value); },
  3:  (draft, value) => { draft.strings.prompt = value; },
  7:  (draft, value) => { draft.strings.styleName = internTrim(draft, value); },
  10: (draft, value) => { setPointComponent(draft.points, "insertion", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "insertion", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "insertion", "z", 30, value); },
  11: (draft, value) => { setPointComponent(draft.points, "alignment", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "alignment", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "alignment", "z", 31, value); },
  40: (draft, value) => { draft.numbers.height = parseNumberGroup(40, value); },
  41: (draft, value) => { draft.numbers.widthFactor = parseNumberGroup(41, value); },
  50: (draft, value) => { draft.numbers.rotation = parseNumberGroup(50, value); },
  51: (draft, value) => { draft.numbers.obliqueAngle = parseNumberGroup(51, value); },
  70: (draft, value) => { draft.numbers.flags = parseIntegerGroup(70, value); },
  71: (draft, value) => { draft.numbers.generationFlags = parseIntegerGroup(71, value); },
  72: (draft, value) => { draft.numbers.hJustification = parseIntegerGroup(72, value); },
  73: (draft, value) => { draft.numbers.fieldLength = parseIntegerGroup(73, value); },
  74: (draft, value) => { draft.numbers.vJustification = parseIntegerGroup(74, value); },
  // MTEXT-ATTDEF background fill
  90: (draft, value) => { draft.numbers.backgroundFill = parseIntegerGroup(90, value); },
  63: (draft, value) => { draft.numbers.backgroundFillColorACI = parseIntegerGroup(63, value); },
  420: (draft, value) => { draft.numbers.backgroundFillColorRGB = parseIntegerGroup(420, value); },
  441: (draft, value) => { draft.numbers.backgroundTransparency = parseIntegerGroup(441, value); },
  45: (draft, value) => { draft.numbers.fillBoxScale = parseNumberGroup(45, value); },
  // Lock position / duplicate record cloning flag
  280: (draft, value) => { draft.numbers.lockPosition = parseIntegerGroup(280, value); },
};

// ATTRIB shares the same layout as ATTDEF except group 1 holds the
// current attribute VALUE (not a default) and there is no group 3
// (prompt) — the prompt lives on the matching ATTDEF.
const ATTRIB_GROUP_READERS: Record<number, GroupReader> = {
  ...ATTDEF_GROUP_READERS,
  1: (draft, value) => { draft.strings.value = value; },
  // Group 3 is not a prompt on ATTRIB. Some older DXF dialects put a
  // mirror of the tag there; we capture it as a fallback but give
  // group 2 precedence when both are present.
  3: (draft, value) => {
    if (draft.strings.tag === undefined) {
      draft.strings.tag = internTrim(draft, value);
    }
  },
};

// SEQEND has no body data beyond the common entity props. We keep an
// empty dispatch table so the entity reader falls through to common
// readers for the handle (5), owner (330), layer (8), etc.
const SEQEND_GROUP_READERS: Record<number, GroupReader> = {};

/* ── HATCH ─────────────────────────────────────────── */

// Lazily create the gradient accumulator on the first gradient group so
// non-gradient hatches keep `gradient` undefined. Defaults mirror
// model.ts hatchGradientSchema so an absent group leaves a faithful
// value rather than NaN.
function ensureHatchGradient(draft: SupportedEntityDraft): DxfHatchGradient {
  if (draft.hatchGradient === null) {
    draft.hatchGradient = {
      kind: 1,
      reserved: 0,
      oneColor: 0,
      numberOfColors: 2,
      rotation: 0,
      shift: 0,
      tint: 0,
      name: "LINEAR",
      colors: [],
    };
  }
  return draft.hatchGradient;
}

// Build a reader for a SCALAR gradient code (a 450-block field that is not a
// color stop). When the gradient is active (450=1 seen) the value populates the
// accumulator; otherwise (a solid 450=0 block, or a stray code with no 450=1)
// the group is preserved via `extraGroups` so the block round-trips WITHOUT
// making the model `gradient` field defined (Codex #208 iter-3).
function gradientScalarReader(
  code: number,
  apply: (g: DxfHatchGradient, value: string) => void,
): GroupReader {
  return (draft, value) => {
    if (draft.hatchGradientActive) {
      apply(ensureHatchGradient(draft), value);
    } else {
      draft.extraGroups.push({ code, value });
    }
  };
}

// Build a HATCH reader for a code that is boundary geometry while
// `inHatchBoundary`, and pattern-definition / unknown passthrough otherwise.
// The boundary reader (entityParser.hatchBoundary.ts) returns true when it
// consumed the group; on false we preserve the group verbatim via
// `extraGroups` so the pre-boundary-parsing round-trip behaviour (these codes
// previously had no HATCH reader and fell through to extraGroups) is unchanged.
function hatchBoundaryOrPassthrough(code: number): GroupReader {
  const boundaryReader = HATCH_BOUNDARY_READERS[code]!;
  return (draft, value) => {
    if (boundaryReader(draft, value)) return;
    draft.extraGroups.push({ code, value });
  };
}

/**
 * FIX A / FIX 1 — close the boundary section when an UNAMBIGUOUS post-boundary
 * pattern/seed/gradient marker (52/41/47/98/450) arrives while still
 * `inHatchBoundary`. None of these codes is ever valid boundary geometry, so
 * their appearance signals the boundary ended with the closing 75/76 omitted
 * (default-style export). We finalize any open path and drop the gate so the
 * marker reads as its real field instead of boundary passthrough.
 *
 * Returns `true` when the marker should be processed normally, `false` when the
 * declared path count is NOT yet met (a genuinely-malformed marker mid-boundary
 * — preserve it verbatim via extraGroups rather than mis-closing the section).
 * When not in-boundary it returns `true` (nothing to close; process normally).
 */
function closeBoundaryForPostMarker(draft: SupportedEntityDraft, code: number, value: string): boolean {
  if (!draft.inHatchBoundary) return true;
  const target = draft.hatchBoundaryPathTarget;
  const willMeetTarget =
    target !== undefined &&
    draft.hatchBoundaryFinalizedCount + (draft.hatchCurrentPath ? 1 : 0) >= target;
  if (!willMeetTarget) {
    draft.extraGroups.push({ code, value });
    return false;
  }
  finalizeHatchCurrentPath(draft);
  draft.inHatchBoundary = false;
  return true;
}

/**
 * S-HATCH-CUSTOM — the current pattern-definition line being built, creating a
 * fresh one (with zeroed base/offset, empty dashes) when none is open. A group
 * 53 (line angle) opens each line; this also handles a malformed block where a
 * 43/44/45/46/49/79 arrives before its 53 (still attach it to a line rather
 * than dropping the value — никаких silent-drop, ПРИОРИТЕТ #1).
 */
function ensureHatchPatternLine(draft: SupportedEntityDraft): DxfHatchPatternLine {
  if (!draft.hatchCurrentPatternLine) {
    const line: DxfHatchPatternLine = {
      angle: 0,
      base: { x: 0, y: 0 },
      offset: { x: 0, y: 0 },
      dashLengths: [],
    };
    draft.hatchCurrentPatternLine = line;
    draft.hatchPatternLines.push(line);
    // BUG 2 — this line was minted WITHOUT its 53 angle (orphan base/offset/dash
    // arrived first). Mark it so the next 53 fills the angle in-place instead of
    // pushing a duplicate line.
    draft.hatchCurrentPatternLineNeedsAngle = true;
  }
  return draft.hatchCurrentPatternLine;
}

const HATCH_GROUP_READERS: Record<number, GroupReader> = {
  2:  (draft, value) => { draft.strings.patternName = internTrim(draft, value); },
  70: (draft, value) => { draft.numbers.solidFill = parseIntegerGroup(70, value); },
  71: (draft, value) => { draft.numbers.associative = parseIntegerGroup(71, value); },
  75: (draft, value) => {
    // Group 75 (hatch style) is the FIRST code after the boundary section —
    // it closes it. Finalize any still-open path and flip the gate so the
    // pattern-definition codes that follow (77/53/78/79/43/44/45/46) and the
    // seed block (98/10/20) are no longer read as boundary geometry.
    finalizeHatchCurrentPath(draft);
    draft.inHatchBoundary = false;
    draft.numbers.hatchStyle = parseIntegerGroup(75, value);
  },
  76: (draft, value) => {
    // FIX 1 — group 76 (patternType) is the first GUARANTEED post-boundary code
    // when the closing group 75 is OMITTED (default hatchStyle 0 export). Close
    // the boundary defensively so the subsequent 52/41 read as pattern fields,
    // not boundary passthrough. Finalize any still-open path first.
    finalizeHatchCurrentPath(draft);
    draft.inHatchBoundary = false;
    draft.numbers.patternType = parseIntegerGroup(76, value);
  },
  52: (draft, value) => {
    // 52 (patternAngle) is never a boundary code — close the boundary first if
    // it arrives while still in-boundary (75/76 omitted export) — FIX A.
    if (!closeBoundaryForPostMarker(draft, 52, value)) return;
    draft.numbers.patternAngle = parseNumberGroup(52, value);
  },
  41: (draft, value) => {
    // 41 is the pattern scale OUTSIDE the boundary. A 41 is never valid boundary
    // geometry, so when it arrives while still in-boundary it signals the
    // boundary section ENDED with both group 75 AND group 76 omitted (default
    // hatchStyle 0 + patternType 0 export). FIX 1 — finalize any open path, close
    // the section, and read the 41 as the pattern scale (otherwise it would be
    // swallowed to extraGroups and silently reset patternScale on round-trip).
    // Guard on the declared path count being met so a genuinely-malformed 41 in
    // the MIDDLE of the boundary still falls back to the extraGroups passthrough.
    if (!closeBoundaryForPostMarker(draft, 41, value)) return;
    draft.numbers.patternScale = parseNumberGroup(41, value);
  },
  // ── Custom pattern-definition lines (S-HATCH-CUSTOM) ──
  // The pattern-data block (78 + per-line 53/43/44/45/46/79/49) always follows
  // the boundary section and the 75/76/52/41 markers. None of these codes is
  // valid boundary geometry (43/44/45/46/49/53/78/79 are NOT HATCH_BOUNDARY_
  // READERS keys), so they only fire AFTER the boundary closes — defensively
  // close it for the count marker (78), then accumulate per-53.
  // S-HATCH-CUSTOM — pattern double-line flag (DXF group 77: 0=not double,
  // 1=double; ezdxf `Hatch.dxf.pattern_double`). It lives in the entity-level
  // pattern-data block AFTER the boundary closes and AFTER 52/41 (angle/scale),
  // alongside 75/76, BEFORE the 78 pattern-line block. 77 is NEVER a boundary
  // code (not a HATCH_BOUNDARY_READERS key), so it only fires once the boundary
  // section is over — defensively close it for the marker (75/76/41 omitted
  // default-style export) then route it to the typed `doubleLine` model field.
  77: (draft, value) => {
    if (!closeBoundaryForPostMarker(draft, 77, value)) return;
    draft.numbers.doubleLine = parseIntegerGroup(77, value);
  },
  78: (draft, value) => {
    // 78 = number of pattern-definition lines. Never a boundary code — close the
    // boundary first if 75/76/41 were all omitted (FIX A family).
    if (!closeBoundaryForPostMarker(draft, 78, value)) return;
    draft.hatchPatternLineTarget = parseIntegerGroup(78, value);
  },
  53: (draft, value) => {
    // 53 (pattern line angle) OPENS a new pattern-definition line.
    if (!closeBoundaryForPostMarker(draft, 53, value)) return;
    // BUG 2 — if the current line is an un-angled placeholder (orphan
    // 43/44/45/46/49/79 arrived before this 53 via ensureHatchPatternLine), set
    // its angle IN-PLACE rather than pushing a second line. Otherwise open a new
    // line as usual.
    if (draft.hatchCurrentPatternLine && draft.hatchCurrentPatternLineNeedsAngle) {
      draft.hatchCurrentPatternLine.angle = parseNumberGroup(53, value);
      draft.hatchCurrentPatternLineNeedsAngle = false;
      return;
    }
    const line: DxfHatchPatternLine = {
      angle: parseNumberGroup(53, value),
      base: { x: 0, y: 0 },
      offset: { x: 0, y: 0 },
      dashLengths: [],
    };
    draft.hatchCurrentPatternLine = line;
    draft.hatchPatternLines.push(line);
    draft.hatchCurrentPatternLineNeedsAngle = false;
  },
  43: (draft, value) => {
    ensureHatchPatternLine(draft).base.x = parseNumberGroup(43, value);
  },
  44: (draft, value) => {
    ensureHatchPatternLine(draft).base.y = parseNumberGroup(44, value);
  },
  45: (draft, value) => {
    ensureHatchPatternLine(draft).offset.x = parseNumberGroup(45, value);
  },
  46: (draft, value) => {
    ensureHatchPatternLine(draft).offset.y = parseNumberGroup(46, value);
  },
  // 79 = dash-length item count for the current line. Informational — we
  // accumulate dash lengths per-49 so a miscounted 79 cannot drop a dash;
  // consume it so it does not fall to extraGroups and re-emit out of place.
  79: (_draft, _value) => { /* count marker — accumulation is driven by 49 */ },
  49: (draft, value) => {
    ensureHatchPatternLine(draft).dashLengths.push(parseNumberGroup(49, value));
  },
  91: (draft, value) => {
    // Group 91 (boundary-path count) OPENS the boundary section.
    const count = parseIntegerGroup(91, value);
    draft.numbers.boundaryCount = count;
    draft.inHatchBoundary = true;
    draft.inHatchSeed = false;
    // FIX 1 — record the declared path count so the boundary section can be
    // closed once that many paths are finalized, even when the closing group 75
    // is omitted (default hatchStyle 0 export). Reset the finalized counter for
    // this entity's boundary.
    draft.hatchBoundaryPathTarget = count;
    draft.hatchBoundaryFinalizedCount = 0;
    draft.hatchBoundaryPathsComplete = false;
  },
  92: (draft, value) => {
    // Group 92 (path-type flag) — always opens a new boundary path. A 92 only
    // occurs inside the boundary section; defensively ensure the gate is set.
    draft.inHatchBoundary = true;
    readHatchPath92(draft, value);
  },
  47: (draft, value) => {
    // 47 (pixelSize) is never a boundary code — close the boundary first if it
    // arrives while still in-boundary (75/76 omitted export) — FIX A.
    if (!closeBoundaryForPostMarker(draft, 47, value)) return;
    draft.numbers.pixelSize = parseNumberGroup(47, value);
  },
  98: (draft, value) => {
    // Group 98 (seed-point count) opens the seed-point block: a trailing
    // 10/20 pair is now a seed point. The boundary section is normally closed
    // by group 75, but 98 is itself an unambiguous post-boundary marker — when
    // 75/76/41 were all omitted, 98 can be the first code after the last path.
    // FIX A — finalize any still-open path + drop the gate before reading.
    finalizeHatchCurrentPath(draft);
    draft.numbers.seedPointCount = parseIntegerGroup(98, value);
    draft.inHatchBoundary = false;
    draft.inHatchSeed = true;
  },
  10: (draft, value) => {
    // Disambiguate by parser state:
    //   inHatchBoundary → boundary vertex / edge point (helper readers)
    //   inHatchSeed     → seed point (group 10/20 after group 98)
    //   otherwise       → the leading 10/20/30 elevation point BEFORE 91 —
    //                     capture X/Y/Z (S-HATCH-ELEVATION-XY) so the full
    //                     WCS elevation point round-trips (do NOT mint a
    //                     spurious seed point).
    if (HATCH_BOUNDARY_READERS[10]!(draft, value)) return;
    if (draft.inHatchSeed) {
      draft.hatchSeedPoints.push({ x: parseNumberGroup(10, value), y: 0 });
      return;
    }
    // Leading elevation point X (group 10 before 91) — capture it. The Z
    // (group 30) and Y (group 20) are captured by their own readers.
    draft.numbers.elevationX = parseNumberGroup(10, value);
  },
  20: (draft, value) => {
    if (HATCH_BOUNDARY_READERS[20]!(draft, value)) return;
    if (draft.inHatchSeed) {
      const last = draft.hatchSeedPoints[draft.hatchSeedPoints.length - 1];
      if (last) {
        last.y = parseNumberGroup(20, value);
      }
      return;
    }
    // Leading elevation point Y (group 20 before 91).
    draft.numbers.elevationY = parseNumberGroup(20, value);
  },
  // ── Boundary-only shared codes (gated on inHatchBoundary) ──
  // Each delegates to the boundary reader; when the boundary section is closed
  // the reader returns false and we preserve the group via extraGroups so the
  // existing pattern-definition / passthrough behaviour is unchanged.
  11: hatchBoundaryOrPassthrough(11),
  21: hatchBoundaryOrPassthrough(21),
  // FIX C — spline-edge start/end tangent vectors (12/22, 13/23). Consumed only
  // while in-boundary AND the current edge is a spline (we tessellate from
  // control points, so the tangents are informational); preserved verbatim via
  // extraGroups otherwise so any non-spline use round-trips unchanged.
  12: hatchBoundaryOrPassthrough(12),
  22: hatchBoundaryOrPassthrough(22),
  13: hatchBoundaryOrPassthrough(13),
  23: hatchBoundaryOrPassthrough(23),
  40: hatchBoundaryOrPassthrough(40),
  50: hatchBoundaryOrPassthrough(50),
  51: hatchBoundaryOrPassthrough(51),
  42: hatchBoundaryOrPassthrough(42),
  72: hatchBoundaryOrPassthrough(72),
  // Group 73 for HATCH is BOUNDARY-ONLY: inside a boundary path it is the
  // polyline is-closed flag (POLYLINE form) or the arc/elliptic-arc CCW flag
  // (EDGE-BASED form). Outside the boundary it is preserved verbatim. The hatch
  // pattern DOUBLE-LINE flag is group 77 (handled below) — NOT 73. (Top-level 73
  // is reserved by the DXF reference for MPolygon boundary annotation; we never
  // mint a doubleLine from a 73.)
  73: hatchBoundaryOrPassthrough(73),
  74: hatchBoundaryOrPassthrough(74),
  93: hatchBoundaryOrPassthrough(93),
  94: hatchBoundaryOrPassthrough(94),
  95: hatchBoundaryOrPassthrough(95),
  96: hatchBoundaryOrPassthrough(96),
  97: hatchBoundaryOrPassthrough(97),
  // FIX 2 — boundary point Z (30/31). The writer emits 30/31 for every boundary
  // point via writePoint; consume-and-drop them while in-boundary (model points
  // are 2D) so they do NOT accumulate in extraGroups and re-emit as stray 30/31
  // OUTSIDE the boundary on import/export cycles. Outside the boundary the
  // boundary reader returns false and the group is the HATCH ELEVATION Z
  // (group 30, emitted either as the leading elevation point BEFORE 91 or as a
  // lone post-boundary 30 when 75/76/41 are omitted). S-HATCH-ELEVATION-XY —
  // route it to the typed `elevation` (Z) field instead of extraGroups so the
  // elevation point is modelled (X/Y captured by the 10/20 readers above) and
  // round-trips via the writer's `geom.elevation` emit (no extraGroups dup).
  30: (draft, value) => {
    if (HATCH_BOUNDARY_READERS[30]!(draft, value)) return;
    draft.numbers.elevation = parseNumberGroup(30, value);
  },
  // A lone 31 (mirrors 30) is never the HATCH elevation in practice (DXF
  // stores elevation as group 30); preserve any out-of-boundary 31 verbatim.
  31: hatchBoundaryOrPassthrough(31),
  // FIX 3 — associative source-boundary object handles (330 after 97 > 0). While
  // in-boundary these are SOURCE handles, NOT the entity owner: consume + ignore
  // so they never overwrite `common.ownerHandle` (read in the AcDbEntity section
  // before group 91). Outside the boundary, delegate to the common owner-handle
  // reader so the HATCH's real owner round-trips unchanged.
  330: (draft, value) => {
    if (readHatchBoundarySourceHandle(draft, value)) return;
    COMMON_GROUP_READERS[330]!(draft, value);
  },
  // ── Gradient fill (group 450..470) ──
  // 450 is the gradient FLAG: 1 = gradient, 0 = solid (a solid HATCH may still
  // carry a 450..470 block). Only 450=1 activates gradient parsing + the model
  // `gradient` field; otherwise the whole block is passthrough so it round-trips
  // without misclassifying a solid hatch. 463 begins a color stop; the 63/421
  // that follow attach to that stop while the stop context is open.
  450: (draft, value) => {
    // 450 (gradient flag) is never a boundary code — close the boundary first if
    // it arrives while still in-boundary (75/76/41 omitted export) — FIX A. A
    // mid-boundary 450 (count not met) is preserved as passthrough by the helper.
    if (!closeBoundaryForPostMarker(draft, 450, value)) return;
    if (parseIntegerGroup(450, value) === 1) {
      ensureHatchGradient(draft).kind = 1;
      draft.hatchGradientActive = true;
    } else {
      // 450=0 (solid) or any non-1 — do NOT create a gradient; preserve the
      // flag (and the block that follows) as passthrough.
      draft.hatchGradientActive = false;
      draft.extraGroups.push({ code: 450, value });
    }
  },
  451: gradientScalarReader(451, (g, v) => { g.reserved = parseIntegerGroup(451, v); }),
  452: gradientScalarReader(452, (g, v) => { g.oneColor = parseIntegerGroup(452, v); }),
  453: gradientScalarReader(453, (g, v) => { g.numberOfColors = parseIntegerGroup(453, v); }),
  460: gradientScalarReader(460, (g, v) => { g.rotation = parseNumberGroup(460, v); }),
  461: gradientScalarReader(461, (g, v) => { g.shift = parseNumberGroup(461, v); }),
  462: gradientScalarReader(462, (g, v) => { g.tint = parseNumberGroup(462, v); }),
  463: (draft, value) => {
    if (!draft.hatchGradientActive) {
      // A color stop inside a non-gradient (450=0) block — preserve verbatim.
      draft.extraGroups.push({ code: 463, value });
      return;
    }
    // Start a new color stop with its position; ACI (63) / true-color (421)
    // follow and attach only when present, so a stop without 63 keeps
    // `aci: undefined` and round-trips without an injected `63 0`.
    ensureHatchGradient(draft).colors.push({
      position: parseNumberGroup(463, value),
    });
    draft.hatchGradientStopOpen = true;
  },
  63: (draft, value) => {
    // Attach to the current gradient stop ONLY while a gradient is active AND a
    // stop context is open (between a 463 and the closing 470). Otherwise — a
    // bare non-gradient fill colour, a solid 450=0 block, or a 63 appended after
    // 470 — preserve via the generic `extraGroups` passthrough (never mint a
    // gradient nor overwrite a sealed stop).
    const grad = draft.hatchGradient;
    const last =
      draft.hatchGradientActive && draft.hatchGradientStopOpen && grad
        ? grad.colors[grad.colors.length - 1]
        : undefined;
    if (last) {
      last.aci = parseIntegerGroup(63, value);
    } else {
      draft.extraGroups.push({ code: 63, value });
    }
  },
  421: (draft, value) => {
    // Same gate as 63.
    const grad = draft.hatchGradient;
    const last =
      draft.hatchGradientActive && draft.hatchGradientStopOpen && grad
        ? grad.colors[grad.colors.length - 1]
        : undefined;
    if (last) {
      last.trueColor = parseIntegerGroup(421, value);
    } else {
      draft.extraGroups.push({ code: 421, value });
    }
  },
  470: (draft, value) => {
    if (!draft.hatchGradientActive) {
      draft.extraGroups.push({ code: 470, value });
      return;
    }
    ensureHatchGradient(draft).name = internTrim(draft, value);
    // 470 is the LAST code of the gradient block — SEAL the whole gradient
    // context: clear BOTH the stop flag AND the active flag. Otherwise a later
    // gradient-like group appended after the block (a 63/421 stop code OR a
    // scalar 460/461/470/… preserved as an extraGroup) would be consumed on the
    // next parse and overwrite an already-parsed field (rotation/name/…) instead
    // of round-tripping as an extra (Codex #208 iter-4, generalising iter-2).
    draft.hatchGradientStopOpen = false;
    draft.hatchGradientActive = false;
  },
};

/* ── VIEWPORT ──────────────────────────────────────── */

const VIEWPORT_GROUP_READERS: Record<number, GroupReader> = {
  10: (draft, value) => { setPointComponent(draft.points, "center", "x", 10, value); },
  20: (draft, value) => { setPointComponent(draft.points, "center", "y", 20, value); },
  30: (draft, value) => { setPointComponent(draft.points, "center", "z", 30, value); },
  40: (draft, value) => { draft.numbers.width = parseNumberGroup(40, value); },
  41: (draft, value) => { draft.numbers.height = parseNumberGroup(41, value); },
  12: (draft, value) => { setPointComponent(draft.points, "viewCenter", "x", 12, value); },
  22: (draft, value) => { setPointComponent(draft.points, "viewCenter", "y", 22, value); },
  45: (draft, value) => { draft.numbers.viewHeight = parseNumberGroup(45, value); },
  16: (draft, value) => { setPointComponent(draft.points, "viewDir", "x", 16, value); },
  26: (draft, value) => { setPointComponent(draft.points, "viewDir", "y", 26, value); },
  36: (draft, value) => { setPointComponent(draft.points, "viewDir", "z", 36, value); },
  17: (draft, value) => { setPointComponent(draft.points, "viewTarget", "x", 17, value); },
  27: (draft, value) => { setPointComponent(draft.points, "viewTarget", "y", 27, value); },
  37: (draft, value) => { setPointComponent(draft.points, "viewTarget", "z", 37, value); },
  51: (draft, value) => { draft.numbers.viewTwistAngle = parseNumberGroup(51, value); },
  42: (draft, value) => { draft.numbers.lensLength = parseNumberGroup(42, value); },
  43: (draft, value) => { draft.numbers.frontClipZ = parseNumberGroup(43, value); },
  44: (draft, value) => { draft.numbers.backClipZ = parseNumberGroup(44, value); },
  68: (draft, value) => { draft.numbers.viewportStatus = parseIntegerGroup(68, value); },
  69: (draft, value) => { draft.numbers.viewportId = parseIntegerGroup(69, value); },
  90: (draft, value) => { draft.numbers.statusBitFlags = parseIntegerGroup(90, value); },
  170: (draft, value) => { draft.numbers.shadePlotMode = parseIntegerGroup(170, value); },
  281: (draft, value) => { draft.numbers.renderMode = parseIntegerGroup(281, value); },
  // ── Lighting block — view brightness / contrast (S-VIEWPORT-A,
  //    audit §12.1 «Contrast», truth-corrected) ──────────────────────
  // DXF group 141 = view BRIGHTNESS, group 142 = view CONTRAST (floats,
  // 2007+/AC1021 lighting block). Source of truth: Autodesk DXF reference
  // VIEWPORT (141 "View brightness", 142 "View contrast") + ezdxf 1.4.3
  // entities/viewport.py acdb_viewport (`view_brightness: DXFAttr(141)`,
  // `view_contrast: DXFAttr(142)`) + real AutoCAD output (АБИ1.dxf emits
  // `141 0.0` / `142 0.0` between 282 and 63/421 on every VIEWPORT).
  //
  // HISTORY (PR-13 mis-map, fixed here): 142 was previously read as
  // `viewportCannoscaleFactor` and 144 as an "AcDbScale handle" — neither
  // exists in the DXF reference, ezdxf, or real files. That mis-read
  // corrupted real AutoCAD imports: the lighting `142 0.0` landed in the
  // model's per-VP annotation-scale factor (a bogus 0). The real per-VP
  // annotation scale is carried by the viewport's extension-dictionary
  // XRECORD "ASDK_XREC_ANNOTATION_SCALE_INFO" (hard pointer 340 →
  // AcDbScale in ACAD_SCALELIST), which round-trips verbatim through the
  // OBJECTS dictionaries / rawObjectGroups passthrough. There is NO 144
  // reader on purpose: an unread 144 falls through to `extraGroups` and
  // round-trips verbatim through the model (preserve-source) instead of
  // being typed here and dropped at the bridge.
  141: (draft, value) => { draft.numbers.viewportBrightness = parseNumberGroup(141, value); },
  142: (draft, value) => { draft.numbers.viewportContrast = parseNumberGroup(142, value); },
  13: (draft, value) => { setPointComponent(draft.points, "snapBase", "x", 13, value); },
  23: (draft, value) => { setPointComponent(draft.points, "snapBase", "y", 23, value); },
  14: (draft, value) => { setPointComponent(draft.points, "snapSpacing", "x", 14, value); },
  24: (draft, value) => { setPointComponent(draft.points, "snapSpacing", "y", 24, value); },
  15: (draft, value) => { setPointComponent(draft.points, "gridSpacing", "x", 15, value); },
  25: (draft, value) => { setPointComponent(draft.points, "gridSpacing", "y", 25, value); },
  // Batch-5 S-VIEWPORT-SNAPGRID — snap/grid scalars. Pre-Batch-5 these
  // codes had NO VIEWPORT reader → fell through to extraGroups and
  // round-tripped only via the opaque S-COMMON-XDATA passthrough. Routing
  // them into typed draft fields here removes them from extraGroups (entity
  // readers take priority over the passthrough), so the writer's typed emit
  // is the SINGLE source — no double-emit.
  50: (draft, value) => { draft.numbers.snapRotation = parseNumberGroup(50, value); },
  61: (draft, value) => { draft.numbers.gridMajor = parseIntegerGroup(61, value); },
  // Group 72 is an INTEGER group-code type (circle-zoom percent). Parse it with
  // the integer parser used for 61/170 — NOT parseNumberGroup — so a fractional
  // source value is normalized to an int (and never re-emitted as a fractional
  // group 72, which would be invalid DXF). Mirrors gridMajor (61).
  72: (draft, value) => { draft.numbers.circleZoomPercent = parseIntegerGroup(72, value); },
  // ── Per-VP frozen-layer + property-override soft-pointers (S-VIEWPORT-
  //    OVERRIDE-331) ──────────────────────────────────────────────────
  // 331 (repeating) — handle of a LAYER record frozen in this viewport.
  // Accumulate (multiple entries may exist). Source of truth: Autodesk DXF
  // reference (AcDbViewport) + ezdxf `Viewport.FROZEN_LAYER_GROUP_CODE = 331`.
  // Pulling them into the typed array here removes them from extraGroups (entity
  // readers take priority), so the writer's typed 331 emit is the SINGLE source.
  331: (draft, value) => { draft.viewportFrozenLayerHandles.push(value.trim()); },
  // 335 / 343 / 344 / 91 — layer VP property-override references. Preserved
  // verbatim as {code, handle} pairs (the exact code per entry is kept so the
  // writer re-emits it byte-faithfully). ezdxf models these as ref_vp_object_1..4
  // and treats them as unreliable; we round-trip them without interpreting them.
  335: (draft, value) => { draft.viewportLayerOverrideHandles.push({ code: 335, handle: value.trim() }); },
  343: (draft, value) => { draft.viewportLayerOverrideHandles.push({ code: 343, handle: value.trim() }); },
  344: (draft, value) => { draft.viewportLayerOverrideHandles.push({ code: 344, handle: value.trim() }); },
  91: (draft, value) => { draft.viewportLayerOverrideHandles.push({ code: 91, handle: value.trim() }); },
  // ── Clipping-boundary handle (S-VIEWPORT-CLIP-HANDLE, EPIC-VIEWPORT-CLIP
  //    slice 1) ───────────────────────────────────────────────────────────
  // 340 — hard-pointer handle to the graphical entity in paperspace that
  // defines this viewport's clip boundary (non-rectangular clip). Source of
  // truth: ezdxf 1.4.3 entities/viewport.py acdb_viewport
  // `"clipping_boundary_handle": DXFAttr(340, default="0", optional=True)` +
  // Autodesk DXF reference AcDbViewport. SINGLE value (not repeating); a later
  // 340 would overwrite (AutoCAD/ezdxf carry exactly one). Captured verbatim
  // (preserve-source — including a literal "0" = no clip) so the writer re-emits
  // it byte-faithfully; pulling it into the typed field here removes it from
  // extraGroups (entity readers take priority over the passthrough), so the
  // writer's typed 340 emit is the SINGLE source — no double-emit.
  //
  // NB: this is the ENTITY-BODY 340 (acdb_viewport attribute). It is a
  // DIFFERENT 340 from the per-VP annotation-scale hard pointer that rides the
  // viewport's extension-dictionary XRECORD "ASDK_XREC_ANNOTATION_SCALE_INFO"
  // → 340 → AcDbScale — that one lives in the OBJECTS section (XRECORD body),
  // is parsed by the OBJECTS/dictionaries passthrough, and is untouched by this
  // entity-body reader. The downstream SPATIAL_FILTER producer + clip resolver
  // + render are the deferred slices 2/3 (S-OBJECTS-SPATIAL-FILTER /
  // S-VIEWPORT-CLIP-RESOLVE); this slice only round-trips the handle.
  340: (draft, value) => { draft.viewportClippingBoundaryHandle = value.trim(); },
};

/* ── MLEADER ───────────────────────────────────────── */

/**
 * In-CONTEXT_DATA (depth > 0) flat-reader allow-list (sub-slice C). The
 * builder OWNS every in-context group (typed contextData + verbatim
 * rawContextGroups) and the byte-faithful writer re-emits the whole CONTEXT_DATA
 * from there, so the dispatch loop's in-context guard does NOT route in-context
 * groups through flat body readers (mis-typing — #256 F2) NOR park them in
 * extraGroups (double-emit). The SOLE exception is this tiny set of flat readers
 * that still feed geometry fields the downstream bridge consumes:
 *   304  MTextData.default_content → geometry.textContents (#258 F1)
 *   340  MTextData.style_handle    → geometry.textStyleHandle (#243 F1; the
 *        reader self-gates on mleaderInContextData so it captures the in-context
 *        text-style handle, never the top-level LeaderLineType 340)
 *   12/22/32 MTextData.insert       → geometry.textLocation
 * These fire in-context but are STILL not parked (the writer re-emits them from
 * contextData/MTextData, not from extraGroups). Every other in-context code is
 * recovered byte-faithfully by the builder.
 */
const MLEADER_INCONTEXT_FLAT_READERS = new Set<number>([304, 340, 12, 22, 32]);

const MLEADER_GROUP_READERS: Record<number, GroupReader> = {
  // MLEADER is very complex; we parse basic fields and store rest in extraGroups
  // Context markers (300-305) end up in extraGroups and are post-processed in finalize
  // #432 r9 E2 / r10 R10-1 — at depth-0 AcDbMLeader BODY, group 10/20/30 is the
  // `block_scale_vector` (ezdxf `export_entity` `write_vertex(10,
  // block_scale_vector)` — UNCONDITIONAL, default (1,1,1)), NOT a leader vertex
  // (leader points live INSIDE the 304 LEADER_LINE CONTEXT_DATA frames, owned by
  // the builder — these readers ONLY fire at depth 0). ACCUMULATE it here and let
  // finalize decide: for a CONTEXT-bearing MLEADER (top-level OR EMBEDDED — the
  // embedded layout puts the body BEFORE the 101/100 wrapper, so no parse-time
  // "context seen" signal is reliable) the accumulated point is the
  // block_scale_vector; a truly marker-less MLEADER keeps the legacy flat-vertex
  // leader fallback (finalize R10-1).
  10: (draft, value) => {
    draft.mleaderCurrentVertices.push({ x: parseNumberGroup(10, value), y: 0, z: 0 });
  },
  20: (draft, value) => {
    const last = draft.mleaderCurrentVertices[draft.mleaderCurrentVertices.length - 1];
    if (last) { last.y = parseNumberGroup(20, value); }
  },
  30: (draft, value) => {
    const last = draft.mleaderCurrentVertices[draft.mleaderCurrentVertices.length - 1];
    if (last) { last.z = parseNumberGroup(30, value); }
  },
  11: (draft, value) => { setPointComponent(draft.points, "doglegVector", "x", 11, value); },
  21: (draft, value) => { setPointComponent(draft.points, "doglegVector", "y", 21, value); },
  31: (draft, value) => { setPointComponent(draft.points, "doglegVector", "z", 31, value); },
  12: (draft, value) => { setPointComponent(draft.points, "textLocation", "x", 12, value); },
  22: (draft, value) => { setPointComponent(draft.points, "textLocation", "y", 22, value); },
  32: (draft, value) => { setPointComponent(draft.points, "textLocation", "z", 32, value); },
  304: (draft, value) => { draft.strings.textContents = value; },
  40: (draft, value) => { draft.numbers.textHeight = parseNumberGroup(40, value); },
  // gap-C body symmetry (ezdxf Scope-6): 41 = dogleg_length (NOT arrow_head_size,
  // which is 42); 42 = arrow_head_size (NOT text rotation — that is MTextData 42
  // inside CONTEXT_DATA). Symmetric to the writer body emit.
  41: (draft, value) => { draft.numbers.doglegLength = parseNumberGroup(41, value); },
  42: (draft, value) => { draft.numbers.arrowheadSize = parseNumberGroup(42, value); },
  // gap-C.2 (FIX #4): body block_rotation = ezdxf Scope-6 group 43 (NOT 46 —
  // 46 is the in-CONTEXT_DATA BlockData rotation, gated). The prior 46 reader is
  // removed; 43 (previously parked verbatim in extraGroups) is now the typed
  // blockRotation. Symmetric to the writer body emit.
  43: (draft, value) => { draft.numbers.blockRotation = parseNumberGroup(43, value); },
  // gap-C.2 (FIX #2): the AcDbMLeader BODY has NO group 44. ezdxf body
  // landing_gap_size is CONTEXT_DATA group 145 (captured in contextData), and a
  // depth-0 group 44 is the block-attribute WIDTH in the trailing AttribData
  // tuple (330/177/44/302, consumed by the context builder). The prior
  // 44→landingGap body reader mis-typed that AttribData width onto landingGap, so
  // it is dropped; a body 44 now flows verbatim to extraGroups (and the builder
  // still reads the AttribData 44 into blockAttributeReferences).
  // gap-C.2 (FIX #1): body 90 = property_override_flags (ezdxf Scope-6). Group 90
  // has no other body reader (the in-context text color 90 is gated; the body
  // 90 previously round-tripped verbatim through extraGroups). It is now the
  // typed propertyOverrideFlags. Symmetric to the writer body emit.
  90: (draft, value) => { draft.numbers.propertyOverrideFlags = parseIntegerGroup(90, value); },
  // gap-C: body 170 = leader_type (→ leaderLineType); content_type is body 172.
  170: (draft, value) => { draft.numbers.leaderLineType = parseIntegerGroup(170, value); },
  // #256 F1 — at the AcDbMLeader BODY level, group 171 is the LEADER
  // LINEWEIGHT, NOT the text-attachment type. ezdxf `acdb_mleader` defines
  // 171 = `leader_lineweight` (default LINEWEIGHT_BYBLOCK); the
  // text-attachment type lives elsewhere (text_left_attachment_type 173 at
  // the body / 171 INSIDE CONTEXT_DATA — both out of scope for this flat
  // body reader). The previous mapping (171 → textAttachmentType) mis-read a
  // lineweight enum (e.g. -2 = ByBlock) onto a small-enum attachment field
  // and corrupted the value on round-trip. Stored as a string to match the
  // drawing-model `leaderLineWeight` representation ("ByBlock"/raw enum).
  // This reader only runs at CONTEXT_DATA depth 0 (the F2 gate parks an
  // in-context 171 in extraGroups before dispatch).
  171: (draft, value) => { draft.strings.leaderLineWeight = value.trim(); },
  // gap-C: body 172 = content_type (→ contentType). The earlier 172→
  // textAttachmentDirection was WRONG (text_attach_direction is body 271, no
  // typed home → round-trips via extraGroups). Body 173 = text_left_attach
  // (no typed home → extraGroups, NOT leaderLineType which is body 170). Body 43
  // = block_rotation (no longer dogleg_length — that is 41 → routed to
  // extraGroups verbatim so it does not clobber the corrected 41 doglegLength).
  172: (draft, value) => { draft.numbers.contentType = parseIntegerGroup(172, value); },
  // gap-C.2 (FIX #1): body 175 = text_alignment_type (ezdxf Scope-6), NOT
  // property_override_flags (that is body 90, above). Routed to the new typed
  // textAlignmentType field. Symmetric to the writer body emit.
  175: (draft, value) => { draft.numbers.textAlignmentType = parseIntegerGroup(175, value); },
  3:  (draft, value) => { draft.strings.styleName = internTrim(draft, value); },
  // gap-C: 290 = has_landing (→ landingEnabled); 291 = has_dogleg (→
  // doglegEnabled); 292 = has_text_frame (→ textFrameDisplay). Symmetric to the
  // writer. (Prior mapping shifted each by one: 290→frame, 291→landing, 292→dogleg.)
  290: (draft, value) => { draft.numbers.landingEnabled = parseIntegerGroup(290, value); },
  291: (draft, value) => { draft.numbers.doglegEnabled = parseIntegerGroup(291, value); },
  292: (draft, value) => { draft.numbers.textFrameDisplay = parseIntegerGroup(292, value); },
  // gap-C: 176 = block_connection_type (→ blockConnectionType, ezdxf Scope-6).
  176: (draft, value) => { draft.numbers.blockConnectionType = parseIntegerGroup(176, value); },
  // SL-5 P3a — body group 270 = AcDbMLeader `version` (ezdxf default 2), emitted
  // FIRST after the AcDbMLeader marker. Captured typed so the writer re-emits it
  // in canonical position (was previously parked in extraGroups + flushed at the
  // entity tail — out of order). Depth-0 only (the in-context guard runs first).
  270: (draft, value) => { draft.numbers.version = parseIntegerGroup(270, value); },
  // SL-5 F5 (@codex round-2) — LEGACY Edit-DWG compatibility. ezdxf `acdb_mleader`
  // has NO body 48/49 (line-spacing lives in CONTEXT_DATA MTextData 45 factor /
  // 170 style), so the byte-faithful writer neither reads nor INVENTS body 48/49
  // (P3c). But old Edit-DWG files wrote MLEADER line-spacing ONLY as depth-0 body
  // 48 (style) / 49 (factor) with no CONTEXT_DATA; leaving them solely in
  // extraGroups made the drawing-model bridge (which DROPS MLEADER extraGroups
  // wholesale) hydrate the flat textLineSpacingStyle/Factor as DEFAULTS → the
  // app-path export lost the spacing. PROMOTE the depth-0 body 48/49 into the flat
  // transport fields (finalize → geom → bridge → model → projected to the real
  // 45/170 home on the app export, migrating the spacing forward) WHILE ALSO
  // preserving the source bytes in extraGroups so a PURE faithful round-trip
  // re-emits them verbatim (unchanged from the prior extraGroups behaviour — no
  // data loss, no NEW writer emit). These readers fire at CONTEXT_DATA depth 0
  // only (the in-context guard runs first), so an in-context 45/170 is untouched.
  48: (draft, value) => {
    draft.numbers.textLineSpacingStyle = parseIntegerGroup(48, value);
    draft.extraGroups.push({ code: 48, value });
  },
  49: (draft, value) => {
    draft.numbers.textLineSpacingFactor = parseNumberGroup(49, value);
    draft.extraGroups.push({ code: 49, value });
  },
  // Group 340 — text-style handle (AcDbMLeader CONTEXT_DATA / MTextData
  // `style_handle`; ezdxf MTextData.ATTRIBS maps 340 → "style_handle",
  // "handle of TextStyle() table entry"). Captured as an opaque handle
  // here; the parser pass-2 resolver swaps it for the STYLE name.
  //
  // CRITICAL (Codex #243 F1): an MLEADER carries MULTIPLE group-340
  // handles — a TOP-LEVEL one (LeaderLineType handle, emitted OUTSIDE
  // CONTEXT_DATA) AND this text-style handle INSIDE the CONTEXT_DATA /
  // MTextData block. We must ONLY capture `textStyleHandle` when the
  // parser is currently inside CONTEXT_DATA; otherwise we would steal the
  // top-level LeaderLineType 340 and corrupt the leader-line reference on
  // round-trip. The top-level 340 keeps its original handling — preserved
  // verbatim through extraGroups (its pre-PR behaviour) so the writer
  // re-emits it byte-for-byte. CONTEXT_DATA scope is tracked in the main
  // dispatch loop (see the `isMLeader` block) which sets
  // `mleaderInContextData` before each reader runs.
  340: (draft, value) => {
    if (draft.mleaderInContextData) {
      draft.strings.textStyleHandle = value.trim();
    } else {
      // SL-5 P2 — the DEPTH-0 (body) 340 is the AcDbMLeader `style_handle`
      // (ezdxf acdb_mleader.export_entity emits `340 dxf.style_handle` right
      // after CONTEXT_DATA — the MLEADERSTYLE record handle, NOT the leader
      // LINETYPE handle which is body 341). Capture it typed so the writer
      // re-emits it in canonical position; previously it was parked verbatim in
      // extraGroups and flushed at the entity tail. Source-preserved (round-trip).
      draft.strings.leaderStyleHandle = value.trim();
    }
  },
  // gap-C.2 (FIX #3): the AcDbMLeader BODY handles per ezdxf Scope-6 are
  //   341 = leader_linetype_handle (handle → LTYPE; no typed model home → flows
  //         verbatim through extraGroups, so NO reader here — was wrongly mapped
  //         to arrowheadBlock),
  //   342 = arrow_head_handle  (→ arrowheadBlock; was wrongly mapped to blockName),
  //   344 = block_record_handle(→ blockName; previously dropped to extraGroups).
  // Each typed reader is symmetric to the writer body emit; the unmodelled 341
  // round-trips byte-faithfully via extraGroups.
  342: (draft, value) => { draft.strings.arrowheadBlock = internTrim(draft, value); },
  // 344 = block_record_handle: store the RAW handle on its own slot (NOT
  // blockName). The pass-2 resolver (resolveMLeaderBlockHandles) rehydrates the
  // block NAME onto blockName so lookup/render resolves a real block, while the
  // writer re-emits 344 verbatim from this handle (Codex #271).
  344: (draft, value) => { draft.strings.blockRecordHandle = internTrim(draft, value); },
  45: (draft, value) => { draft.numbers.blockScale = parseNumberGroup(45, value); },
};

/* ── ARRAYRECT ─────────────────────────────────────── */

const ARRAYRECT_GROUP_READERS: Record<number, GroupReader> = {
  ...INSERT_GROUP_READERS,
  // Overrides and additions for array-specific data come via xdata/extraGroups
};

/* ── REVCLOUD ──────────────────────────────────────── */

// REVCLOUD is stored as LWPOLYLINE with xdata, so re-use polyline readers
const REVCLOUD_GROUP_READERS: Record<number, GroupReader> = {
  ...LWPOLYLINE_GROUP_READERS
};

/* ── WIPEOUT ──────────────────────────────────────── */

// AcDbWipeout (single subclass, group order per ezdxf entities/image.py
// acdb_image). We capture ONLY the placement frame (insert 10/20, u_pixel 11/21,
// v_pixel 12/22) so finalize can INVERT the on-disk NORMALIZED ±0.5 boundary back
// to world coordinates (reconstructWipeoutWorldVertices — the EXACT ezdxf
// `ImageBase.boundary_path_wcs` inverse of the writer's set_masking_area
// normalization). image_size.y (23) IS captured — it is the `height` term of
// boundary_path_wcs — so a FOREIGN image_size ≠ 1 reconstructs correctly;
// image_size.x (13) is NOT referenced by boundary_path_wcs (u_pixel alone scales
// the x axis), so it is consumed, not stored. Our own writer re-synthesizes
// 13/23 = (1,1) unconditionally, so neither reaches extraGroups.
// All synthesized raster-image scalars (90/340/70/280/281/282/283/360/290) and
// the z-components (30/31/32) are CONSUMED here so they never reach extraGroups
// (the writer re-synthesizes the whole body). Group codes:
//   10/20 — insert (bbox MIN corner) ; 11/21 — u_pixel ; 12/22 — v_pixel
//   13 — image_size.x (unused by boundary_path_wcs) — consumed
//   23 — image_size.y (boundary_path_wcs `height` term) — CAPTURED for the inverse
//   71  — clipping_boundary_type (1 rect / 2 poly) — recomputed on write
//   91  — clip vertex count — recomputed on write
//   14  — clip-boundary vertex X (repeating, NORMALIZED ±0.5)
//   24  — clip-boundary vertex Y (repeating, NORMALIZED ±0.5)
//   280 — frame display flag (0/1)
// We only model boundary + frame; raster-image-specific codes
// (image-def reference 340, brightness 90/91, contrast …) flow
// through extraGroups on the entity's common props.
const WIPEOUT_GROUP_READERS: Record<number, GroupReader> = {
  // Placement frame — captured for the normalized→world inversion at finalize.
  10: (draft, value) => setPointComponent(draft.points, "wipeoutInsert", "x", 10, value),
  20: (draft, value) => setPointComponent(draft.points, "wipeoutInsert", "y", 20, value),
  11: (draft, value) => setPointComponent(draft.points, "wipeoutU", "x", 11, value),
  21: (draft, value) => setPointComponent(draft.points, "wipeoutU", "y", 21, value),
  12: (draft, value) => setPointComponent(draft.points, "wipeoutV", "x", 12, value),
  22: (draft, value) => setPointComponent(draft.points, "wipeoutV", "y", 22, value),
  // image_size (13/23) — the WCS inverse (reconstructWipeoutWorldVertices) mirrors
  // ezdxf `ImageBase.boundary_path_wcs`, which folds image_size.y (the `height`
  // term) into the transform. CAPTURE image_size.y so a FOREIGN image_size ≠ (1,1)
  // reconstructs EXACTLY as ezdxf; image_size.x is not referenced by
  // boundary_path_wcs (u_pixel alone scales x) so it is dropped. The writer
  // re-synthesizes 13/23 = (1,1), so neither reaches extraGroups (no double-emit).
  13: () => {}, // image_size.x — unused by boundary_path_wcs; re-synthesized on write
  23: (draft, value) => {
    draft.numbers.wipeoutImageSizeY = parseNumberGroup(23, value);
  },
  // Synthesized raster-image body — consumed (the writer re-synthesizes) so it
  // never lands in extraGroups. NOTE 280 is `clipping` (not a frame flag) and
  // 30/31/32 are the placement z-components (always 0 in the 2D model).
  90: () => {}, // class_version
  30: () => {}, // insert.z
  31: () => {}, // u_pixel.z
  32: () => {}, // v_pixel.z
  340: () => {}, // image_def_handle (null)
  70: () => {}, // flags
  // 280 has TWO roles across WIPEOUT encodings. In BOTH the standard single-marker
  // form and the AcDbRasterImage body of the pre-SL-1 legacy dual-marker form it
  // is the `clipping` flag (always 1), emitted BEFORE the 14/24 boundary —
  // re-synthesized on write, so dropped. ONLY the pre-SL-1 legacy dual-marker form
  // ADDITIONALLY emits a TRAILING `280 frame` AFTER the boundary (the old writer's
  // final group; ezdxf/AutoCAD's conformant single-marker tail is clip_mode 290,
  // never a trailing 280). Capture a 280 seen once boundary vertices have begun as
  // that legacy frame flag; a 280 before any vertex is the clipping flag and stays
  // dropped. finalize applies wipeoutFrame ONLY for the legacy form, so the
  // standard form keeps its frame default (WIPEOUTFRAME = 1). Without this a
  // hidden legacy frame (`280 0`) flipped back ON to true on open (data loss).
  280: (draft, value) => {
    if (
      draft.wipeoutVertices.length > 0 ||
      draft.wipeoutCurrentVertex !== null
    ) {
      draft.numbers.wipeoutFrame = parseIntegerGroup(280, value);
    }
  },
  281: () => {}, // brightness
  282: () => {}, // contrast
  283: () => {}, // fade
  360: () => {}, // image_def_reactor_handle (null)
  71: () => {}, // clipping_boundary_type — recomputed on write
  91: () => {}, // count_boundary_points — recomputed on write
  // clip_mode (0 = mask outside boundary, 1 = inside). MODELED, not dropped —
  // SL-1's no-op read forced every export to `290 0`, flipping a source
  // `290 1` (inside clip) to outside (data loss). Stored typed on the entity.
  290: (draft, value) => {
    draft.numbers.wipeoutClipMode = parseNumberGroup(290, value);
  },
  14: (draft, value) => {
    // Each group-14 starts a new (normalized) vertex. If a partial vertex was
    // accumulating (which only happens when the file is malformed and writes
    // 14/14 without an intervening 24), flush the orphan with y = 0 to keep the
    // parser tolerant.
    if (draft.wipeoutCurrentVertex !== null) {
      draft.wipeoutVertices.push({
        x: draft.wipeoutCurrentVertex.x ?? 0,
        y: draft.wipeoutCurrentVertex.y ?? 0,
      });
    }
    draft.wipeoutCurrentVertex = { x: parseNumberGroup(14, value) };
  },
  24: (draft, value) => {
    // Group 24 closes the in-flight (normalized) vertex started by group 14.
    if (draft.wipeoutCurrentVertex === null) {
      draft.wipeoutCurrentVertex = {};
    }
    draft.wipeoutCurrentVertex.y = parseNumberGroup(24, value);
    draft.wipeoutVertices.push({
      x: draft.wipeoutCurrentVertex.x ?? 0,
      y: draft.wipeoutCurrentVertex.y,
    });
    draft.wipeoutCurrentVertex = null;
  },
};

const ENTITY_GROUP_READERS: Record<DxfSupportedEntityType, Record<number, GroupReader>> = {
  LINE: LINE_GROUP_READERS,
  POINT: POINT_GROUP_READERS,
  CIRCLE: CIRCLE_GROUP_READERS,
  ARC: ARC_GROUP_READERS,
  ELLIPSE: ELLIPSE_GROUP_READERS,
  SPLINE: SPLINE_GROUP_READERS,
  LWPOLYLINE: LWPOLYLINE_GROUP_READERS,
  TEXT: TEXT_GROUP_READERS,
  MTEXT: MTEXT_GROUP_READERS,
  DIMENSION: DIMENSION_GROUP_READERS,
  INSERT: INSERT_GROUP_READERS,
  HATCH: HATCH_GROUP_READERS,
  VIEWPORT: VIEWPORT_GROUP_READERS,
  MLEADER: MLEADER_GROUP_READERS,
  ARRAYRECT: ARRAYRECT_GROUP_READERS,
  REVCLOUD: REVCLOUD_GROUP_READERS,
  WIPEOUT: WIPEOUT_GROUP_READERS,
  ATTDEF: ATTDEF_GROUP_READERS,
  ATTRIB: ATTRIB_GROUP_READERS,
  SEQEND: SEQEND_GROUP_READERS,
  POLYLINE: POLYLINE_GROUP_READERS,
  VERTEX: VERTEX_GROUP_READERS,
  ACAD_TABLE: ACAD_TABLE_GROUP_READERS,
  OLE2FRAME: OLE2FRAME_GROUP_READERS
};

function readOptionalPoint(
  points: Record<string, MutablePoint>,
  key: string
): DxfPoint3D | undefined {
  const point = points[key];
  if (!point || point.x === undefined || point.y === undefined) {
    return undefined;
  }
  return { x: point.x, y: point.y, z: point.z ?? 0 };
}

// Decode the DXF group-70 bitfield on ATTDEF / ATTRIB entities. Each
// bit corresponds to a documented behaviour flag. We return a typed
// struct so downstream code reads each flag by name instead of
// bit-twiddling a raw integer.
function decodeAttribFlagBits(flags: number): DxfAttribFlagBits {
  return {
    invisible: (flags & 1) !== 0,
    constant: (flags & 2) !== 0,
    verify: (flags & 4) !== 0,
    preset: (flags & 8) !== 0,
    lockPosition: (flags & 16) !== 0,
    multiline: (flags & 32) !== 0,
  };
}

function readOptionalPoint2D(
  points: Record<string, MutablePoint>,
  key: string
): { x: number; y: number } | undefined {
  const point = points[key];
  if (!point || point.x === undefined || point.y === undefined) {
    return undefined;
  }
  return { x: point.x, y: point.y };
}

/**
 * True when a WIPEOUT draft is the pre-SL-1 dual-marker WORLD-coord form — i.e.
 * it carries the `AcDbRasterImage` subclass marker. The conformant ezdxf/AutoCAD
 * wipeout (`ImageBase.export_entity` → single `_SUBCLASS_NAME` = "AcDbWipeout")
 * emits ONE marker; ONLY our pre-SL-1 dual-marker export wrote a second
 * `AcDbRasterImage` marker AND stored the 14/24 boundary in WORLD (not normalized
 * ±0.5) coords, so its presence is the exact, sufficient legacy fingerprint.
 *
 * MARKER-ONLY — deliberately NO coordinate-range clause. A prior heuristic ALSO
 * flagged "any |14|/|24| > 0.5 + ε" as legacy, but that is WRONG: a STANDARD
 * single-marker WIPEOUT/IMAGE whose `image_size` (13/23) ≠ (1,1) legitimately has
 * pixel-space boundary values beyond ±0.5 (image_size 2 → corners ~−0.5 … 1.5).
 * The range clause misclassified such conformant files as legacy → returned raw
 * pixel values instead of the boundary_path_wcs transform → the mask collapsed to
 * the origin. The marker is unambiguous (our old writer ALWAYS emitted it), so
 * marker-only catches every legacy file with NO false positive on image_size ≠ 1.
 *
 * The ONE shared legacy discriminator: `reconstructWipeoutWorldVertices` uses it
 * to read 14/24 as world verbatim (vs the boundary_path_wcs inverse), and
 * finalize uses it to recover the trailing-`280` frame flag (vs the
 * WIPEOUTFRAME = 1 default) — so both agree on exactly what "legacy" means.
 */
function isLegacyWipeoutForm(draft: SupportedEntityDraft): boolean {
  return draft.subclassMarkers.includes("AcDbRasterImage");
}

/**
 * Reconstruct a WIPEOUT's WORLD-space clip boundary from the on-disk NORMALIZED
 * ±0.5 image-space vertices — the exact, IMAGE_SIZE-INDEPENDENT inverse of the
 * writer's ezdxf-`set_masking_area` normalization, applied PER stored vertex so
 * export → import is an identity on the vertex list.
 *
 * ONE exception, mirroring ezdxf `ImageBase.pixel_boundary_path` (image.py): a
 * boundary of EXACTLY 2 vertices is the RECTANGULAR (`71 = 1`) shorthand — the
 * min/max diagonal corners — which readers expand into the 4 rectangle corners
 * `[p0, (p1.x, p0.y), p1, (p0.x, p1.y)]`. We expand in NORMALIZED (pixel) space
 * BEFORE the inverse (exactly as ezdxf, which expands the pixel path then applies
 * the WCS transform) so the resulting polygon is correct even for a rotated
 * u/v frame. This gives the model a valid ≥3-vertex polygon (`wipeoutEntitySchema`
 * requires `.min(3)`); a 2-vertex diagonal alone would import as an undrawn mask.
 *
 * `set_masking_area` (ezdxf entities/image.py) stores each world vertex as
 *   bx = (world.x − insert.x)/‖u‖ − 0.5 ,  by = 0.5 − (world.y − insert.y)/‖v‖
 * with insert = bbox MIN corner, u_pixel = (width,0), v_pixel = (0,height),
 * image_size = (1,1). Its inverse is the EXACT ezdxf `ImageBase.boundary_path_wcs`
 * (entities/image.py, 1.4.3), applied per stored pixel vertex `(bx,by)`:
 *
 *   origin = insert + u·0.5 − v·0.5
 *   world  = origin + u·bx + v·(image_size.y − by)
 *          = insert + u·(bx + 0.5) + v·(image_size.y − by − 0.5)
 *
 * boundary_path_wcs folds `height = image_size.y` (NO division; image_size.x is
 * NOT referenced — u_pixel alone scales the x axis) into the transform, so the
 * inverse is image_size.y-DEPENDENT. For our own writer / AutoCAD / ezdxf wipeouts
 * image_size = (1,1) ⇒ the `v·(1 − by − 0.5) = v·(0.5 − by)` special case, so our
 * export→import round-trip is byte-unchanged; a FOREIGN image_size ≠ 1 (a raster
 * IMAGE re-tagged as a wipeout, or a foreign wipeout carrying image_size 2 with
 * pixel corners ~−0.5 … 1.5) reconstructs EXACTLY as ezdxf's own reader. A prior
 * "image_size-INDEPENDENT" simplification dropped the height term and only held
 * for image_size = 1 — it offset every foreign-image_size vertex by
 * v·(image_size.y − 1). image_size.y is read from group 23 (default 1 when
 * absent). Defaults (insert 0,0 / u 1,0 / v 0,1) keep a body-less malformed
 * record from collapsing.
 *
 * LEGACY (pre-SL-1) FORM — CRITICAL for not corrupting our OWN older exports.
 * Before SL-1 our writer emitted the OLD dual-marker chain
 * (`100 AcDbRasterImage` … `100 AcDbWipeout`) and stored the 14/24 boundary in
 * WORLD coordinates (the pre-SL-1 parser read them back VERBATIM — no inverse).
 * Applying the boundary_path_wcs inverse above to those already-world vertices
 * explodes a 100×50 mask `(0,0)..(100,50)` into ~`(50,25)..(10050,−2475)` — so
 * merely OPEN+SAVE of an existing drawing WE previously exported would silently
 * corrupt the mask. We detect the legacy form by its `AcDbRasterImage` subclass
 * marker (see `isLegacyWipeoutForm`) and read 14/24 as world directly. The
 * discriminator is MARKER-ONLY: the standard ezdxf/AutoCAD wipeout emits a single
 * `AcDbWipeout` marker, and ONLY our pre-SL-1 dual-marker export carried
 * AcDbRasterImage, so its presence ⇒ legacy. A former coordinate-range fallback
 * (|14|/|24| > 0.5 ⇒ legacy) was REMOVED: a conformant single-marker body with
 * image_size ≠ 1 legitimately exceeds ±0.5, so the range clause misclassified it.
 * Legacy ⇒ world verbatim (no inverse). The NEXT export upgrades to the standard
 * normalized single-marker form (upgrade-on-save), which then round-trips exactly.
 * Standard form (single AcDbWipeout) ⇒ the boundary_path_wcs inverse above.
 */
function reconstructWipeoutWorldVertices(
  draft: SupportedEntityDraft,
): { x: number; y: number }[] {
  // ezdxf pixel_boundary_path: a 2-vertex boundary is the rectangular (`71 = 1`)
  // shorthand — the min/max diagonal corners — expanded to the 4 rectangle
  // corners. This is FORM-INDEPENDENT (done before the world/normalized branch):
  // for the legacy path the corners are already WORLD, for the standard path they
  // are PIXEL space expanded BEFORE the WCS inverse (exactly as ezdxf, which
  // expands the pixel path then transforms). Either way the model gets a valid
  // ≥3-vertex polygon (wipeoutEntitySchema requires `.min(3)`).
  let verts = draft.wipeoutVertices;
  if (verts.length === 2) {
    const p0 = verts[0]!;
    const p1 = verts[1]!;
    verts = [p0, { x: p1.x, y: p0.y }, p1, { x: p0.x, y: p1.y }];
  }

  // Detect the legacy pre-SL-1 dual-marker WORLD-coord form (see docstring): the
  // `AcDbRasterImage` marker — MARKER-ONLY (the coordinate-range clause was
  // removed because a standard image_size ≠ 1 body legitimately exceeds ±0.5).
  // This is the ONE shared discriminator finalize also uses for the trailing-280
  // frame flag.
  if (isLegacyWipeoutForm(draft)) {
    // 14/24 already ARE world coordinates — return verbatim, NO inverse.
    return verts.map(({ x, y }) => ({ x, y }));
  }

  const insert = readOptionalPoint2D(draft.points, "wipeoutInsert") ?? {
    x: 0,
    y: 0,
  };
  const u = readOptionalPoint2D(draft.points, "wipeoutU") ?? { x: 1, y: 0 };
  const v = readOptionalPoint2D(draft.points, "wipeoutV") ?? { x: 0, y: 1 };
  // image_size.y — the `height` term of ezdxf boundary_path_wcs (group 23,
  // default 1). For our own writer image_size = (1,1) ⇒ v·(1 − by − 0.5) =
  // v·(0.5 − by); a foreign image_size ≠ 1 shifts the y-axis exactly as ezdxf's
  // own reader.
  const imgH = draft.numbers.wipeoutImageSizeY ?? 1;
  return verts.map(({ x: bx, y: by }) => ({
    x: insert.x + u.x * (bx + 0.5) + v.x * (imgH - by - 0.5),
    y: insert.y + u.y * (bx + 0.5) + v.y * (imgH - by - 0.5),
  }));
}

/**
 * Resolve the final leaderRoots for an MLEADER.
 *
 * The CONTEXT_DATA nested-tree builder (`MLeaderContextBuilder`) already decoded
 * the proper leader hierarchy DURING the group-walk — each `LEADER{` → a
 * root/node, each `LEADER_LINE{` → a line with its OWN vertices — for the first
 * CONTEXT_DATA block. When that produced at least one leader line we return it
 * verbatim. Otherwise (a marker-less MLEADER, or a markered one whose leaders
 * carried no leader lines) we fall back to wrapping the flat accumulated
 * vertices into a single root, exactly as the legacy `buildMLeaderRoots`
 * fallback did — so the synthesis-from-leaderRoots writer round-trip for those
 * shapes is unchanged.
 */
function resolveMLeaderLeaderRoots(
  builderResult: MLeaderBuilderResult,
  flatLines: DxfMLeaderLeaderLine[],
  points: Record<string, MutablePoint>,
): DxfMLeaderLeaderRoot[] {
  if (
    builderResult.hasContextMarkers &&
    builderResult.leaderRoots.length > 0
  ) {
    return builderResult.leaderRoots;
  }
  // Marker-less MLEADER (or context markers that produced no leader lines):
  // wrap the flat accumulated vertices into a single root (backward compat).
  if (flatLines.length === 0) return [];
  const doglegVec = readOptionalPoint(points, "doglegVector");
  const node: DxfMLeaderLeaderNode = { lines: flatLines };
  if (doglegVec) node.doglegVector = doglegVec;
  return [{ nodes: [node] }];
}

function finalizeSupportedEntity(draft: SupportedEntityDraft): DxfSupportedEntity {
  const extrusionPoint = draft.points.__extrusion__;

  if (extrusionPoint) {
    draft.common.extrusion = {
      x: extrusionPoint.x ?? 0,
      y: extrusionPoint.y ?? 0,
      z: extrusionPoint.z ?? 1
    };
  }

  const base: {
    kind: "supported-entity";
    common: DxfCommonEntityProps;
    subclassMarkers: string[];
    extraGroups: DxfGroup[];
    xdata?: DxfXDataEntry[];
  } = {
    kind: "supported-entity" as const,
    common: draft.common,
    subclassMarkers: draft.subclassMarkers,
    extraGroups: draft.extraGroups
  };

  if (draft.xdata.length > 0) {
    base.xdata = draft.xdata;
  }

  // SL-2 (DXF R2018) — reclassify a real DXF entity carrying our internal
  // discriminator back to the internal type. The writer serializes REVCLOUD as
  // `0 LWPOLYLINE` (REVCLOUD is NOT a DXF entity), so on read we must re-detect
  // it or the internal type is lost: a `0 LWPOLYLINE` carrying `RevcloudProps`
  // XData IS a revision cloud (this is exactly how AutoCAD itself marks a
  // polyline as a revcloud). REVCLOUD_GROUP_READERS === LWPOLYLINE_GROUP_READERS,
  // so the walked draft already holds the right data; only the type label is
  // corrected here, before the finalize dispatch below. A legacy file that still
  // carries `0 REVCLOUD` skips this (type already set) and finalizes directly —
  // backward compatible. (ARRAYRECT is deferred — see writer.ts
  // DXF_ENTITY_NAME_OVERRIDE note.)
  if (
    draft.type === "LWPOLYLINE" &&
    draft.xdata.some((x) => x.appName === REVCLOUD_PROPS_APPID)
  ) {
    draft.type = "REVCLOUD";
  }

  if (draft.type === "LINE") {
    const entity: DxfLineEntity = {
      ...base,
      type: "LINE",
      geometry: {
        start: readPoint(draft.points, "start", "LINE start"),
        end: readPoint(draft.points, "end", "LINE end")
      }
    };
    return entity;
  }

  if (draft.type === "POINT") {
    const geom: DxfPointEntity["geometry"] = {
      location: readPoint(draft.points, "location", "POINT location")
    };
    if (draft.numbers.rotation !== undefined) {
      geom.rotation = draft.numbers.rotation;
    }
    const entity: DxfPointEntity = {
      ...base,
      type: "POINT",
      geometry: geom
    };
    return entity;
  }

  if (draft.type === "CIRCLE") {
    const radius = readRequiredNumber(draft.numbers, "radius", "CIRCLE radius");
    if (radius <= 0) {
      throw new Error("CIRCLE radius must be greater than zero");
    }
    const entity: DxfCircleEntity = {
      ...base,
      type: "CIRCLE",
      geometry: {
        center: readPoint(draft.points, "center", "CIRCLE center"),
        radius
      }
    };
    return entity;
  }

  if (draft.type === "ARC") {
    const radius = readRequiredNumber(draft.numbers, "radius", "ARC radius");
    if (radius <= 0) {
      throw new Error("ARC radius must be greater than zero");
    }
    const entity: DxfArcEntity = {
      ...base,
      type: "ARC",
      geometry: {
        center: readPoint(draft.points, "center", "ARC center"),
        radius,
        startAngle: readRequiredNumber(draft.numbers, "startAngle", "ARC start angle"),
        endAngle: readRequiredNumber(draft.numbers, "endAngle", "ARC end angle")
      }
    };
    return entity;
  }

  if (draft.type === "ELLIPSE") {
    const ratio = readRequiredNumber(draft.numbers, "ratio", "ELLIPSE ratio");
    if (ratio <= 0) {
      throw new Error("ELLIPSE ratio must be greater than zero");
    }
    const entity: DxfEllipseEntity = {
      ...base,
      type: "ELLIPSE",
      geometry: {
        center: readPoint(draft.points, "center", "ELLIPSE center"),
        majorAxisEndpoint: readPoint(draft.points, "majorAxis", "ELLIPSE major-axis endpoint"),
        ratio,
        startParam: draft.numbers.startParam ?? 0,
        endParam: draft.numbers.endParam ?? 2 * Math.PI,
      }
    };
    return entity;
  }

  if (draft.type === "SPLINE") {
    flushSplineControlPoint(draft);
    flushSplineFitPoint(draft);
    const flags = draft.numbers.flags ?? 0;
    const degree = draft.numbers.degree ?? 3;
    const geom: DxfSplineEntity["geometry"] = {
      degree,
      flags,
      closed: (flags & 1) !== 0,
      periodic: (flags & 2) !== 0,
      rational: (flags & 4) !== 0,
      planar: (flags & 8) !== 0,
      linear: (flags & 16) !== 0,
      knots: draft.splineKnots,
      controlPoints: draft.splineControlPoints,
      weights: draft.splineWeights,
      fitPoints: draft.splineFitPoints,
    };
    const startTangent = readOptionalPoint(draft.points, "splineStartTangent");
    if (startTangent) geom.startTangent = startTangent;
    const endTangent = readOptionalPoint(draft.points, "splineEndTangent");
    if (endTangent) geom.endTangent = endTangent;
    if (draft.numbers.knotTolerance !== undefined) {
      geom.knotTolerance = draft.numbers.knotTolerance;
    }
    if (draft.numbers.controlPointTolerance !== undefined) {
      geom.controlPointTolerance = draft.numbers.controlPointTolerance;
    }
    if (draft.numbers.fitTolerance !== undefined) {
      geom.fitTolerance = draft.numbers.fitTolerance;
    }
    return { ...base, type: "SPLINE", geometry: geom } satisfies DxfSplineEntity;
  }

  if (draft.type === "LWPOLYLINE") {
    flushLwVertex(draft);
    const flags = draft.numbers.flags ?? 0;
    const geom: DxfLwPolylineEntity["geometry"] = {
      vertices: draft.lwVertices,
      closed: (flags & 1) !== 0,
      plinegen: (flags & 128) !== 0,
      vertexCount: draft.numbers.vertexCount ?? draft.lwVertices.length
    };
    if (draft.numbers.constantWidth !== undefined) geom.constantWidth = draft.numbers.constantWidth;
    if (draft.numbers.elevation !== undefined) geom.elevation = draft.numbers.elevation;
    return { ...base, type: "LWPOLYLINE", geometry: geom } satisfies DxfLwPolylineEntity;
  }

  if (draft.type === "TEXT") {
    const geom: DxfTextEntity["geometry"] = {
      insertionPoint: readPoint(draft.points, "insertion", "TEXT insertion point"),
      height: readRequiredNumber(draft.numbers, "height", "TEXT height"),
      // T9 PR-3 — decode `\U+XXXX` escapes (symmetric inverse of the writer).
      contents: decodeDxfUnicode(draft.strings.contents ?? "")
    };
    const ap = readOptionalPoint(draft.points, "alignment");
    if (ap) geom.alignmentPoint = ap;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    if (draft.numbers.widthFactor !== undefined) geom.widthFactor = draft.numbers.widthFactor;
    if (draft.numbers.obliqueAngle !== undefined) geom.obliqueAngle = draft.numbers.obliqueAngle;
    if (draft.numbers.hJustification !== undefined) geom.hJustification = draft.numbers.hJustification;
    if (draft.numbers.vJustification !== undefined) geom.vJustification = draft.numbers.vJustification;
    if (draft.numbers.generationFlags !== undefined) geom.generationFlags = draft.numbers.generationFlags;
    return { ...base, type: "TEXT", geometry: geom } satisfies DxfTextEntity;
  }

  if (draft.type === "MTEXT") {
    // T9 PR-3 — JOIN the group-3 chunks + final group-1 FIRST, THEN decode
    // `\U+XXXX`. Decoding after the join rejoins any escape that a foreign
    // writer split across a group-3 chunk boundary before it is decoded.
    const fullContents = decodeDxfUnicode(
      draft.mtextParts.join("") + (draft.strings.contents ?? ""),
    );
    const geom: DxfMTextEntity["geometry"] = {
      insertionPoint: readPoint(draft.points, "insertion", "MTEXT insertion point"),
      nominalHeight: readRequiredNumber(draft.numbers, "nominalHeight", "MTEXT nominal height"),
      contents: fullContents
    };
    if (draft.numbers.referenceWidth !== undefined) geom.referenceWidth = draft.numbers.referenceWidth;
    if (draft.numbers.attachmentPoint !== undefined) geom.attachmentPoint = draft.numbers.attachmentPoint;
    if (draft.numbers.drawingDirection !== undefined) geom.drawingDirection = draft.numbers.drawingDirection;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    if (draft.numbers.lineSpacingStyle !== undefined) geom.lineSpacingStyle = draft.numbers.lineSpacingStyle;
    if (draft.numbers.lineSpacingFactor !== undefined) geom.lineSpacingFactor = draft.numbers.lineSpacingFactor;
    // xAxisDirection (codes 11/21/31) — read into draft.points under "xAxisDir"
    const xAxisDir = readOptionalPoint(draft.points, "xAxisDir");
    if (xAxisDir) geom.xAxisDirection = xAxisDir;
    // Reference-rectangle extents (codes 42/43)
    if (draft.numbers.horizontalWidth !== undefined) geom.horizontalWidth = draft.numbers.horizontalWidth;
    if (draft.numbers.verticalHeight !== undefined) geom.verticalHeight = draft.numbers.verticalHeight;
    // Background-fill block (codes 90/63/420/441/45)
    if (draft.numbers.backgroundFillSetting !== undefined) geom.backgroundFillSetting = draft.numbers.backgroundFillSetting;
    if (draft.numbers.backgroundFillColorACI !== undefined) geom.backgroundFillColorACI = draft.numbers.backgroundFillColorACI;
    if (draft.numbers.backgroundFillColorRGB !== undefined) geom.backgroundFillColorRGB = draft.numbers.backgroundFillColorRGB;
    if (draft.numbers.backgroundTransparency !== undefined) geom.backgroundTransparency = draft.numbers.backgroundTransparency;
    if (draft.numbers.fillBoxScale !== undefined) geom.fillBoxScale = draft.numbers.fillBoxScale;
    // Column block (codes 75/76/78/79/48/49). 78/79 are integer flags in DXF;
    // the typed model exposes them as booleans.
    if (draft.numbers.columnType !== undefined) geom.columnType = draft.numbers.columnType;
    if (draft.numbers.columnCount !== undefined) geom.columnCount = draft.numbers.columnCount;
    if (draft.numbers.columnFlowReversed !== undefined) geom.columnFlowReversed = draft.numbers.columnFlowReversed !== 0;
    if (draft.numbers.columnAutoheight !== undefined) geom.columnAutoheight = draft.numbers.columnAutoheight !== 0;
    if (draft.numbers.columnWidth !== undefined) geom.columnWidth = draft.numbers.columnWidth;
    if (draft.numbers.columnGutter !== undefined) geom.columnGutter = draft.numbers.columnGutter;
    // geom.columnHeights — typed read-only projection of the per-column heights
    // decoded from the `101 AcDbMText` embedded column block (group 72 count +
    // repeated 46s; see the inEmbeddedObject capture in parseSupportedEntity).
    // A body-level group 46 (scalar defined column height, before the `101`
    // marker) is NEVER captured into this array — the #254 corruption guard.
    // The heights ALSO remain in extraGroups, so the writer re-emits the whole
    // embedded block verbatim (single source of truth, no double-emit); this
    // field is purely additive for typed consumers (bridge / compact / render).
    if (draft.mtextEmbeddedColumnHeights.length > 0) {
      geom.columnHeights = [...draft.mtextEmbeddedColumnHeights];
    }
    return { ...base, type: "MTEXT", geometry: geom } satisfies DxfMTextEntity;
  }

  if (draft.type === "DIMENSION") {
    const geom: DxfDimensionEntity["geometry"] = {
      dimensionType: draft.numbers.dimensionType ?? 0,
      defPoint: readPoint(draft.points, "defPoint", "DIMENSION defPoint"),
      textMidpoint: readPoint(draft.points, "textMidpoint", "DIMENSION textMidpoint")
    };
    if (draft.strings.dimensionText !== undefined) geom.dimensionText = decodeDxfUnicode(draft.strings.dimensionText); // T9 PR-3 decode
    if (draft.strings.anonymousBlockName !== undefined) geom.anonymousBlockName = draft.strings.anonymousBlockName;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    const ext1 = readOptionalPoint(draft.points, "ext1");
    if (ext1) geom.extLine1Point = ext1;
    const ext2 = readOptionalPoint(draft.points, "ext2");
    if (ext2) geom.extLine2Point = ext2;
    const tp = readOptionalPoint(draft.points, "thirdPoint");
    if (tp) geom.thirdPoint = tp;
    const ap = readOptionalPoint(draft.points, "arcPoint");
    if (ap) geom.arcPoint = ap;
    if (draft.numbers.linearAngle !== undefined) geom.linearAngle = draft.numbers.linearAngle;
    if (draft.numbers.measurement !== undefined) geom.measurement = draft.numbers.measurement;
    if (draft.numbers.leaderLength !== undefined) geom.leaderLength = draft.numbers.leaderLength;
    if (draft.numbers.obliqueAngle !== undefined) geom.obliqueAngle = draft.numbers.obliqueAngle;
    if (draft.numbers.textRotation !== undefined) geom.textRotation = draft.numbers.textRotation;
    if (draft.numbers.textAngleRelativeToDimLine !== undefined) geom.textAngleRelativeToDimLine = draft.numbers.textAngleRelativeToDimLine;
    if (draft.numbers.attachmentPoint !== undefined) geom.attachmentPoint = draft.numbers.attachmentPoint;
    // S-DIMENSION-EXTRAS — version(280), lineSpacingStyle(72)/Factor(41),
    // clone insertion point (12/22/32). thickness(39) + extrusion
    // (210/220/230) ride the COMMON channel (base.common) like every other
    // OCS entity, so they need no DIMENSION-specific transfer here.
    if (draft.numbers.dimVersion !== undefined) geom.version = draft.numbers.dimVersion;
    if (draft.numbers.dimLineSpacingStyle !== undefined) geom.lineSpacingStyle = draft.numbers.dimLineSpacingStyle;
    if (draft.numbers.dimLineSpacingFactor !== undefined) geom.lineSpacingFactor = draft.numbers.dimLineSpacingFactor;
    const dimInsert = readOptionalPoint(draft.points, "dimInsert");
    if (dimInsert) geom.insertionPoint = dimInsert;
    return { ...base, type: "DIMENSION", geometry: geom } satisfies DxfDimensionEntity;
  }

  if (draft.type === "INSERT") {
    const geom: DxfInsertEntity["geometry"] = {
      blockName: draft.strings.blockName ?? "",
      insertionPoint: readPoint(draft.points, "insertion", "INSERT insertion point")
    };
    if (draft.numbers.scaleX !== undefined) geom.scaleX = draft.numbers.scaleX;
    if (draft.numbers.scaleY !== undefined) geom.scaleY = draft.numbers.scaleY;
    if (draft.numbers.scaleZ !== undefined) geom.scaleZ = draft.numbers.scaleZ;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    if (draft.numbers.attributesFollow !== undefined) {
      geom.attributesFollow = draft.numbers.attributesFollow !== 0;
    }
    if (draft.numbers.colCount !== undefined) geom.columnCount = draft.numbers.colCount;
    if (draft.numbers.rowCount !== undefined) geom.rowCount = draft.numbers.rowCount;
    if (draft.numbers.colSpacing !== undefined) geom.columnSpacing = draft.numbers.colSpacing;
    if (draft.numbers.rowSpacing !== undefined) geom.rowSpacing = draft.numbers.rowSpacing;
    const ocs = readOptionalPoint(draft.points, "__extrusion__");
    if (ocs && !(ocs.x === 0 && ocs.y === 0 && ocs.z === 1)) {
      geom.extrusion = ocs;
    }
    // Phase N.4 — capture per-INSERT representation data linkage from
    // XData (AcDbBlockRepETag) and the 102 {ACAD_XDICTIONARY} block.
    // The parameter override map and visibility-state id are filled in
    // later by the bridge once it has resolved the OBJECTS-section
    // dictionary chain (the entity parser sees only handle references
    // here, not the dictionary payload).
    const representationData: NonNullable<DxfInsertEntity["geometry"]["representationData"]> = {};
    let hasRepresentationData = false;
    if (draft.common.extensionDictionaryHandle !== undefined) {
      representationData.dataHandle = draft.common.extensionDictionaryHandle;
      hasRepresentationData = true;
    }
    const repEtagXData = draft.xdata.find(
      (x) => x.appName === "AcDbBlockRepETag",
    );
    if (repEtagXData) {
      // AcDbBlockRepETag carries one or more 1071 (long) entries that
      // form the etag. We concatenate them as a stable string key.
      const etagParts = repEtagXData.groups
        .filter((g) => g.code === 1071 || g.code === 1070)
        .map((g) => g.value.trim());
      if (etagParts.length > 0) {
        representationData.etag = etagParts.join(":");
        hasRepresentationData = true;
      }
    }
    if (hasRepresentationData) {
      geom.representationData = representationData;
    }
    return { ...base, type: "INSERT", geometry: geom } satisfies DxfInsertEntity;
  }

  if (draft.type === "ATTDEF") {
    const flags = draft.numbers.flags ?? 0;
    const geom: DxfAttdefEntity["geometry"] = {
      insertionPoint: readPoint(draft.points, "insertion", "ATTDEF insertion point"),
      height: readRequiredNumber(draft.numbers, "height", "ATTDEF height"),
      // T9 PR-3 — decode `\U+XXXX` content (tag is decoded via internTrim).
      defaultValue: decodeDxfUnicode(draft.strings.defaultValue ?? ""),
      tag: draft.strings.tag ?? "",
      prompt: decodeDxfUnicode(draft.strings.prompt ?? ""),
      flags,
      flagBits: decodeAttribFlagBits(flags),
    };
    const alignment = readOptionalPoint(draft.points, "alignment");
    if (alignment) geom.alignmentPoint = alignment;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    if (draft.numbers.widthFactor !== undefined) geom.widthFactor = draft.numbers.widthFactor;
    if (draft.numbers.obliqueAngle !== undefined) geom.obliqueAngle = draft.numbers.obliqueAngle;
    if (draft.numbers.generationFlags !== undefined) geom.generationFlags = draft.numbers.generationFlags;
    if (draft.numbers.hJustification !== undefined) geom.hJustification = draft.numbers.hJustification;
    if (draft.numbers.fieldLength !== undefined) geom.fieldLength = draft.numbers.fieldLength;
    if (draft.numbers.vJustification !== undefined) geom.vJustification = draft.numbers.vJustification;
    if (draft.numbers.lockPosition !== undefined) geom.lockPosition = draft.numbers.lockPosition !== 0;
    if (draft.numbers.backgroundFill !== undefined) geom.backgroundFill = draft.numbers.backgroundFill;
    if (draft.numbers.backgroundFillColorACI !== undefined) geom.backgroundFillColorACI = draft.numbers.backgroundFillColorACI;
    if (draft.numbers.backgroundFillColorRGB !== undefined) geom.backgroundFillColorRGB = draft.numbers.backgroundFillColorRGB;
    if (draft.numbers.backgroundTransparency !== undefined) geom.backgroundTransparency = draft.numbers.backgroundTransparency;
    if (draft.numbers.fillBoxScale !== undefined) geom.fillBoxScale = draft.numbers.fillBoxScale;
    return { ...base, type: "ATTDEF", geometry: geom } satisfies DxfAttdefEntity;
  }

  if (draft.type === "ATTRIB") {
    const flags = draft.numbers.flags ?? 0;
    const geom: DxfAttribEntity["geometry"] = {
      insertionPoint: readPoint(draft.points, "insertion", "ATTRIB insertion point"),
      height: readRequiredNumber(draft.numbers, "height", "ATTRIB height"),
      // T9 PR-3 — decode `\U+XXXX` content (tag is decoded via internTrim).
      value: decodeDxfUnicode(draft.strings.value ?? ""),
      tag: draft.strings.tag ?? "",
      flags,
      flagBits: decodeAttribFlagBits(flags),
    };
    const alignment = readOptionalPoint(draft.points, "alignment");
    if (alignment) geom.alignmentPoint = alignment;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    if (draft.numbers.widthFactor !== undefined) geom.widthFactor = draft.numbers.widthFactor;
    if (draft.numbers.obliqueAngle !== undefined) geom.obliqueAngle = draft.numbers.obliqueAngle;
    if (draft.numbers.generationFlags !== undefined) geom.generationFlags = draft.numbers.generationFlags;
    if (draft.numbers.hJustification !== undefined) geom.hJustification = draft.numbers.hJustification;
    if (draft.numbers.fieldLength !== undefined) geom.fieldLength = draft.numbers.fieldLength;
    if (draft.numbers.vJustification !== undefined) geom.vJustification = draft.numbers.vJustification;
    if (draft.numbers.lockPosition !== undefined) geom.lockPosition = draft.numbers.lockPosition !== 0;
    if (draft.numbers.backgroundFill !== undefined) geom.backgroundFill = draft.numbers.backgroundFill;
    if (draft.numbers.backgroundFillColorACI !== undefined) geom.backgroundFillColorACI = draft.numbers.backgroundFillColorACI;
    if (draft.numbers.backgroundFillColorRGB !== undefined) geom.backgroundFillColorRGB = draft.numbers.backgroundFillColorRGB;
    if (draft.numbers.backgroundTransparency !== undefined) geom.backgroundTransparency = draft.numbers.backgroundTransparency;
    if (draft.numbers.fillBoxScale !== undefined) geom.fillBoxScale = draft.numbers.fillBoxScale;
    return { ...base, type: "ATTRIB", geometry: geom } satisfies DxfAttribEntity;
  }

  if (draft.type === "SEQEND") {
    // SEQEND has no geometry — it's a pure marker that terminates the
    // ATTRIB sequence (or the POLYLINE vertex sequence, for other kinds
    // of complex entities). We preserve the common props (handle, owner,
    // layer) so the writer can round-trip the DXF.
    return { ...base, type: "SEQEND", geometry: {} } satisfies DxfSeqendEntity;
  }

  if (draft.type === "POLYLINE") {
    // The `vertices` / `seqend` fields are filled by the ENTITIES- and
    // BLOCKS-section grouping passes (parser.ts / streamingParser.ts /
    // sectionParsers.ts), exactly like an INSERT's `attribs`: a VERTEX is a
    // sibling record in the file, not a nested one.
    const geom: DxfPolylineEntity["geometry"] = {
      elevation: readOptionalPoint(draft.points, "elevation") ?? point3D(0, 0, 0),
      // Group 70 is required by the DXF reference (it carries the 2D/3D/mesh
      // discriminator). A file that omits it means a plain open 2D polyline —
      // 0 is the DXF-documented default, not an invented one.
      flags: draft.numbers.flags ?? 0,
      vertices: [],
    };
    if (draft.numbers.verticesFollow !== undefined) {
      geom.verticesFollow = draft.numbers.verticesFollow;
    }
    if (draft.numbers.defaultStartWidth !== undefined) {
      geom.defaultStartWidth = draft.numbers.defaultStartWidth;
    }
    if (draft.numbers.defaultEndWidth !== undefined) {
      geom.defaultEndWidth = draft.numbers.defaultEndWidth;
    }
    if (draft.numbers.meshMCount !== undefined) {
      geom.meshMCount = draft.numbers.meshMCount;
    }
    if (draft.numbers.meshNCount !== undefined) {
      geom.meshNCount = draft.numbers.meshNCount;
    }
    if (draft.numbers.smoothSurfaceMDensity !== undefined) {
      geom.smoothSurfaceMDensity = draft.numbers.smoothSurfaceMDensity;
    }
    if (draft.numbers.smoothSurfaceNDensity !== undefined) {
      geom.smoothSurfaceNDensity = draft.numbers.smoothSurfaceNDensity;
    }
    if (draft.numbers.surfaceType !== undefined) {
      geom.surfaceType = draft.numbers.surfaceType;
    }
    return { ...base, type: "POLYLINE", geometry: geom } satisfies DxfPolylineEntity;
  }

  if (draft.type === "VERTEX") {
    const geom: DxfVertexEntity["geometry"] = {
      location: readOptionalPoint(draft.points, "location") ?? point3D(0, 0, 0),
    };
    if (draft.numbers.startWidth !== undefined) geom.startWidth = draft.numbers.startWidth;
    if (draft.numbers.endWidth !== undefined) geom.endWidth = draft.numbers.endWidth;
    if (draft.numbers.bulge !== undefined) geom.bulge = draft.numbers.bulge;
    if (draft.numbers.flags !== undefined) geom.flags = draft.numbers.flags;
    if (draft.numbers.curveFitTangent !== undefined) {
      geom.curveFitTangent = draft.numbers.curveFitTangent;
    }
    if (draft.numbers.vertexId !== undefined) geom.vertexId = draft.numbers.vertexId;
    // Poly-face FACE record indices (71/72/73/74). Collected per-code so the
    // writer re-emits exactly the codes the file authored. A CONTIGUOUS run
    // starting at 71 — the only shape AutoCAD and ezdxf ever write — becomes
    // the typed `faceVertexIndices` array. A sparse/non-prefix set (e.g. 71
    // and 73 with no 72) cannot be expressed by an ordered array without
    // silently re-coding the values, so those groups are parked verbatim in
    // extraGroups instead: preserved exactly, never guessed.
    const faceValues: number[] = [];
    let contiguous = true;
    for (const code of VERTEX_FACE_INDEX_CODES) {
      const value = draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}${code}`];
      if (value === undefined) {
        contiguous = false;
        continue;
      }
      if (!contiguous) {
        // A gap already occurred — this set is not a prefix run.
        contiguous = false;
        faceValues.length = 0;
        break;
      }
      faceValues.push(value);
    }
    if (faceValues.length > 0) {
      geom.faceVertexIndices = faceValues;
    } else {
      for (const code of VERTEX_FACE_INDEX_CODES) {
        const value = draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}${code}`];
        if (value !== undefined) {
          draft.extraGroups.push({ code, value: String(value) });
        }
      }
    }
    return { ...base, type: "VERTEX", geometry: geom } satisfies DxfVertexEntity;
  }

  if (draft.type === "ACAD_TABLE") {
    const geom: DxfAcadTableEntity["geometry"] = {
      blockName: draft.strings.blockName ?? "",
      insertionPoint:
        readOptionalPoint(draft.points, "insertion") ?? point3D(0, 0, 0),
      rowHeights: draft.tableRowHeights,
      columnWidths: draft.tableColumnWidths,
      contentGroups: draft.tableContentGroups,
      cachedGraphicsChunks: draft.binaryChunks,
    };
    const horizontal = readOptionalPoint(draft.points, "horizontalDirection");
    if (horizontal) geom.horizontalDirection = horizontal;
    if (draft.numbers.tableVersion !== undefined) geom.version = draft.numbers.tableVersion;
    if (draft.strings.tableStyleHandle !== undefined) {
      geom.tableStyleHandle = draft.strings.tableStyleHandle;
    }
    if (draft.strings.blockRecordHandle !== undefined) {
      geom.blockRecordHandle = draft.strings.blockRecordHandle;
    }
    if (draft.numbers.valueFlag !== undefined) geom.valueFlag = draft.numbers.valueFlag;
    if (draft.numbers.rowCount !== undefined) geom.rowCount = draft.numbers.rowCount;
    if (draft.numbers.columnCount !== undefined) geom.columnCount = draft.numbers.columnCount;
    if (draft.numbers.overrideFlag !== undefined) geom.overrideFlag = draft.numbers.overrideFlag;
    if (draft.numbers.borderColorOverrideFlag !== undefined) {
      geom.borderColorOverrideFlag = draft.numbers.borderColorOverrideFlag;
    }
    if (draft.numbers.borderLineweightOverrideFlag !== undefined) {
      geom.borderLineweightOverrideFlag = draft.numbers.borderLineweightOverrideFlag;
    }
    if (draft.numbers.borderVisibilityOverrideFlag !== undefined) {
      geom.borderVisibilityOverrideFlag = draft.numbers.borderVisibilityOverrideFlag;
    }
    if (draft.numbers.cachedGraphicsByteCount !== undefined) {
      geom.cachedGraphicsByteCount = draft.numbers.cachedGraphicsByteCount;
    }
    return { ...base, type: "ACAD_TABLE", geometry: geom } satisfies DxfAcadTableEntity;
  }

  if (draft.type === "OLE2FRAME") {
    const geom: DxfOle2FrameEntity["geometry"] = {
      upperLeft: readOptionalPoint(draft.points, "upperLeft") ?? point3D(0, 0, 0),
      lowerRight: readOptionalPoint(draft.points, "lowerRight") ?? point3D(0, 0, 0),
      binaryChunks: draft.binaryChunks,
    };
    if (draft.numbers.oleVersion !== undefined) geom.oleVersion = draft.numbers.oleVersion;
    if (draft.strings.objectDescription !== undefined) {
      geom.objectDescription = draft.strings.objectDescription;
    }
    if (draft.numbers.oleObjectType !== undefined) {
      geom.oleObjectType = draft.numbers.oleObjectType;
    }
    if (draft.numbers.tileModeDescriptor !== undefined) {
      geom.tileModeDescriptor = draft.numbers.tileModeDescriptor;
    }
    if (draft.numbers.drawAspect !== undefined) geom.drawAspect = draft.numbers.drawAspect;
    if (draft.numbers.binaryByteCount !== undefined) {
      geom.binaryByteCount = draft.numbers.binaryByteCount;
    }
    if (draft.strings.oleTerminator !== undefined) {
      geom.terminator = draft.strings.oleTerminator;
    }
    return { ...base, type: "OLE2FRAME", geometry: geom } satisfies DxfOle2FrameEntity;
  }

  if (draft.type === "HATCH") {
    // Flush any boundary path still open at entity end. AutoCAD always emits a
    // group 75 (which closes the section) but the writer's edge-based output
    // and hand-authored DXF may omit both 75 and 97; this guarantees the last
    // path is finalized rather than dropped.
    finalizeHatchCurrentPath(draft);
    const geom: DxfHatchEntity["geometry"] = {
      patternName: draft.strings.patternName ?? "ANSI31",
      solidFill: (draft.numbers.solidFill ?? 0) !== 0,
      associative: (draft.numbers.associative ?? 0) !== 0,
      boundaryPaths: draft.hatchBoundaryPaths
    };
    if (draft.numbers.hatchStyle !== undefined) geom.hatchStyle = draft.numbers.hatchStyle;
    if (draft.numbers.patternType !== undefined) geom.patternType = draft.numbers.patternType;
    if (draft.numbers.patternAngle !== undefined) geom.patternAngle = draft.numbers.patternAngle;
    if (draft.numbers.patternScale !== undefined) geom.patternScale = draft.numbers.patternScale;
    // S-HATCH-CUSTOM — pattern double-line flag (group 77, pattern data) +
    // custom pattern-definition lines (78 + 53/43/44/45/46/79/49). doubleLine is
    // emitted only when the source carried a 77; customPatternLines only when
    // the source had ≥1 defining line (solid/predefined hatches stay undefined).
    if (draft.numbers.doubleLine !== undefined) {
      geom.doubleLine = draft.numbers.doubleLine !== 0;
    }
    if (draft.hatchPatternLines.length > 0) {
      geom.customPatternLines = draft.hatchPatternLines;
    }
    if (draft.numbers.elevation !== undefined) geom.elevation = draft.numbers.elevation;
    // S-HATCH-ELEVATION-XY — leading 10/20 of the WCS elevation point.
    if (draft.numbers.elevationX !== undefined) geom.elevationX = draft.numbers.elevationX;
    if (draft.numbers.elevationY !== undefined) geom.elevationY = draft.numbers.elevationY;
    // S-HATCH-PIXELSIZE — DXF group 47 (parsed but previously dropped).
    if (draft.numbers.pixelSize !== undefined) geom.pixelSize = draft.numbers.pixelSize;
    // S-HATCH-EXTRUSION — SINGLE-CHANNEL: HATCH carries its OCS normal ONLY on
    // the typed `geometry.extrusion`, never on `common.extrusion`. `finalize-
    // SupportedEntity` copied the parsed 210/220/230 onto `common.extrusion`
    // (generic OCS path); MOVE it to `geom.extrusion` and DELETE the common
    // copy so there is no stale dual channel the writer / bridge could disagree
    // on. The identity (0,0,1) normal is dropped entirely (absent ⇒ default).
    if (
      draft.common.extrusion &&
      !(
        draft.common.extrusion.x === 0 &&
        draft.common.extrusion.y === 0 &&
        draft.common.extrusion.z === 1
      )
    ) {
      geom.extrusion = draft.common.extrusion;
    }
    // Always strip the common copy for HATCH (move, not copy) — including the
    // identity case, so the COMMON group-writer never emits a HATCH 210/220/230
    // and editing only `geometry.extrusion` cannot leave a stale common value.
    delete draft.common.extrusion;
    if (draft.hatchSeedPoints.length > 0) geom.seedPoints = draft.hatchSeedPoints;
    if (draft.hatchGradient !== null) geom.gradient = draft.hatchGradient;
    return { ...base, type: "HATCH", geometry: geom } satisfies DxfHatchEntity;
  }

  if (draft.type === "VIEWPORT") {
    const geom: DxfViewportEntity["geometry"] = {
      center: readPoint(draft.points, "center", "VIEWPORT center"),
      width: readRequiredNumber(draft.numbers, "width", "VIEWPORT width"),
      height: readRequiredNumber(draft.numbers, "height", "VIEWPORT height")
    };
    const vc = readOptionalPoint2D(draft.points, "viewCenter");
    if (vc) geom.viewCenter = vc;
    if (draft.numbers.viewHeight !== undefined) geom.viewHeight = draft.numbers.viewHeight;
    const vd = readOptionalPoint(draft.points, "viewDir");
    if (vd) geom.viewDirection = vd;
    const vt = readOptionalPoint(draft.points, "viewTarget");
    if (vt) geom.viewTarget = vt;
    if (draft.numbers.viewTwistAngle !== undefined) geom.viewTwistAngle = draft.numbers.viewTwistAngle;
    if (draft.numbers.lensLength !== undefined) geom.lensLength = draft.numbers.lensLength;
    if (draft.numbers.frontClipZ !== undefined) geom.frontClipZ = draft.numbers.frontClipZ;
    if (draft.numbers.backClipZ !== undefined) geom.backClipZ = draft.numbers.backClipZ;
    if (draft.numbers.viewportStatus !== undefined) geom.viewportStatus = draft.numbers.viewportStatus;
    if (draft.numbers.viewportId !== undefined) geom.viewportId = draft.numbers.viewportId;
    if (draft.numbers.statusBitFlags !== undefined) geom.statusBitFlags = draft.numbers.statusBitFlags;
    if (draft.numbers.shadePlotMode !== undefined) geom.shadePlotMode = draft.numbers.shadePlotMode;
    if (draft.numbers.renderMode !== undefined) geom.renderMode = draft.numbers.renderMode;
    // Derive displayLocked from bit flags (bit 0x4000 = zoom locking ON)
    if (draft.numbers.statusBitFlags !== undefined) {
      geom.displayLocked = (draft.numbers.statusBitFlags & 0x4000) !== 0;
    }
    const snapBase = readOptionalPoint2D(draft.points, "snapBase");
    if (snapBase) geom.snapBase = snapBase;
    const snapSpacing = readOptionalPoint2D(draft.points, "snapSpacing");
    if (snapSpacing) geom.snapSpacing = snapSpacing;
    const gridSpacing = readOptionalPoint2D(draft.points, "gridSpacing");
    if (gridSpacing) geom.gridSpacing = gridSpacing;
    // Batch-5 S-VIEWPORT-SNAPGRID — snap/grid scalars (50/61/72).
    if (draft.numbers.snapRotation !== undefined) geom.snapRotation = draft.numbers.snapRotation;
    if (draft.numbers.gridMajor !== undefined) geom.gridMajor = draft.numbers.gridMajor;
    if (draft.numbers.circleZoomPercent !== undefined) geom.circleZoomPercent = draft.numbers.circleZoomPercent;
    // S-VIEWPORT-A — lighting block (DXF 141 brightness, 142 contrast).
    // Set only when present so a pre-2007 viewport (no lighting block)
    // reads these as undefined (absence-preserving round-trip).
    if (draft.numbers.viewportBrightness !== undefined) {
      geom.brightness = draft.numbers.viewportBrightness;
    }
    if (draft.numbers.viewportContrast !== undefined) {
      geom.contrast = draft.numbers.viewportContrast;
    }
    // S-VIEWPORT-OVERRIDE-331 — per-VP frozen-layer handles (331) + property-
    // override soft-pointers (335/343/344/91). Set only when present so an
    // ordinary viewport reads these as undefined (round-trip-identical).
    if (draft.viewportFrozenLayerHandles.length > 0) {
      geom.frozenLayerHandles = draft.viewportFrozenLayerHandles.slice();
    }
    if (draft.viewportLayerOverrideHandles.length > 0) {
      geom.layerOverrideHandles = draft.viewportLayerOverrideHandles.slice();
    }
    // S-VIEWPORT-CLIP-HANDLE — clipping-boundary handle (340). Set only when a
    // body 340 was seen so an unclipped viewport reads it as undefined
    // (absence-preserving round-trip).
    if (draft.viewportClippingBoundaryHandle !== undefined) {
      geom.clippingBoundaryHandle = draft.viewportClippingBoundaryHandle;
    }
    return { ...base, type: "VIEWPORT", geometry: geom } satisfies DxfViewportEntity;
  }

  if (draft.type === "MLEADER") {
    // Single-descent CONTEXT_DATA decode (collapses the former
    // buildMLeaderRoots / extractBlockAttributeReferences /
    // extractAnnotativeContexts passes — built DURING the group-walk).
    const builderResult = (draft.mleaderBuilder ?? new MLeaderContextBuilder()).finalize();

    // #432 r10 R10-1 — classify the accumulated depth-0 body 10/20/30. For a
    // CONTEXT-bearing MLEADER (top-level OR EMBEDDED) it is the
    // `block_scale_vector`; `builderResult.hasContextMarkers` is layout-
    // independent (set when the builder sees the 300 marker, whether inside the
    // 101/100 embedded wrapper — parsed AFTER the body — or at top level), so it
    // fixes the embedded case a parse-time ordinal gate could not. Per ezdxf
    // leader vertices ONLY occur inside a LEADER_LINE frame, so a conformant body
    // carries exactly one 10/20/30 = the block scale. A truly marker-less MLEADER
    // (no CONTEXT_DATA) keeps the legacy flat-vertex leader fallback below.
    if (
      builderResult.hasContextMarkers &&
      draft.mleaderCurrentVertices.length > 0
    ) {
      const p = draft.mleaderCurrentVertices[0]!;
      draft.points.blockScaleVector = { x: p.x, y: p.y, z: p.z };
      draft.mleaderCurrentVertices.length = 0; // consumed — NOT leader vertices
    }

    // Flush any remaining flat vertices into mleaderLines (the marker-less
    // fallback path; markered MLEADERs build their hierarchy in the builder).
    if (draft.mleaderCurrentVertices.length > 0) {
      draft.mleaderLines.push({ vertices: [...draft.mleaderCurrentVertices] });
    }

    const leaderRoots = resolveMLeaderLeaderRoots(
      builderResult,
      draft.mleaderLines,
      draft.points,
    );

    // Codex #268 — legacy Block-MLEADER group-170 fallback. The MODERN AcDbMLeader
    // body stores leader_type in 170 and content_type in 172 (handled above);
    // since OUR writer ALWAYS emits 172 (`g.contentType ?? 2`), a body with a
    // lone 170 and NO 172 can only come from a pre-modern / external source. Some
    // legacy Block MLEADERs encoded the CONTENT type in 170 with no 172; the
    // modern reader routes that 170 → leaderLineType and defaults contentType to 2
    // (MText), silently FLIPPING a legacy Block MLEADER to MText on load (data
    // loss). Reinterpret the lone 170 as the legacy content_type ONLY when there
    // is corroborating BLOCK-content evidence (an inline BlockData / has_block 296
    // / a block record handle / a block name) — this NEVER fires for our own
    // output (always has 172) nor for a modern external MText MLEADER that omitted
    // the default 172 (no block evidence), so the modern 170=leader_type /
    // 172=content_type path is untouched. Bounded strictly to the no-172 +
    // block-evidence case (round-trip memory: corroborate, never infer from a lone
    // incidental group).
    const hasBlockContentEvidence =
      builderResult.contextData?.blockData !== undefined ||
      builderResult.contextData?.hasBlock === true ||
      draft.strings.blockRecordHandle !== undefined ||
      (draft.strings.blockName !== undefined && draft.strings.blockName !== "");
    const legacyContentFrom170 =
      draft.numbers.contentType === undefined &&
      draft.numbers.leaderLineType !== undefined &&
      hasBlockContentEvidence;
    const geom: DxfMLeaderEntity["geometry"] = {
      contentType: legacyContentFrom170
        ? draft.numbers.leaderLineType ?? 2 // legacy: 170 carried the content_type
        : draft.numbers.contentType ?? 2,
      // #432 r11 R11-1 — the synthetic single-vertex leader is a RENDER fallback
      // for a MARKER-LESS / authored MLEADER only. A CONTEXT-bearing MLEADER
      // (hasContextMarkers) with zero decoded leaders is a legitimate content-only
      // / leaderless multileader — ezdxf `MLeaderContext.export_dxf` emits ZERO
      // `302 LEADER{` frames for it, so injecting the synthetic vertex FABRICATES a
      // phantom leader to the origin on re-export (PRIORITY #1 geometry
      // fabrication). Keep leaderRoots EMPTY for the context-bearing case; only the
      // marker-less fallback keeps the synthetic default (for its render fallback).
      leaderRoots:
        leaderRoots.length > 0
          ? leaderRoots
          : builderResult.hasContextMarkers
            ? []
            : [{ nodes: [{ lines: [{ vertices: [point3D(0, 0, 0)] }] }] }],
    };
    if (draft.strings.textContents !== undefined) geom.textContents = decodeDxfUnicode(draft.strings.textContents); // T9 PR-3 — MLEADER 304 content decode
    if (draft.strings.textStyleHandle !== undefined) geom.textStyleHandle = draft.strings.textStyleHandle;
    if (draft.numbers.textHeight !== undefined) geom.textHeight = draft.numbers.textHeight;
    if (draft.numbers.textRotation !== undefined) geom.textRotation = draft.numbers.textRotation;
    if (draft.numbers.arrowheadSize !== undefined) geom.arrowheadSize = draft.numbers.arrowheadSize;
    if (draft.strings.arrowheadBlock !== undefined) geom.arrowheadBlock = draft.strings.arrowheadBlock;
    const tl = readOptionalPoint(draft.points, "textLocation");
    if (tl) geom.textLocation = tl;
    if (draft.strings.styleName !== undefined) geom.styleName = draft.strings.styleName;
    // SL-5 P2 — body 340 (MLEADERSTYLE handle) captured typed, source-preserved.
    if (draft.strings.leaderStyleHandle !== undefined) geom.leaderStyleHandle = draft.strings.leaderStyleHandle;
    // SL-5 P3a — body 270 (AcDbMLeader version), source-preserved in position.
    if (draft.numbers.version !== undefined) geom.version = draft.numbers.version;
    if (draft.numbers.landingEnabled !== undefined) geom.landingEnabled = draft.numbers.landingEnabled !== 0;
    if (draft.numbers.doglegEnabled !== undefined) geom.doglegEnabled = draft.numbers.doglegEnabled !== 0;
    if (draft.numbers.doglegLength !== undefined) geom.doglegLength = draft.numbers.doglegLength;
    // gap-C.2 (FIX #2): geom.landingGap is no longer projected from a body 44
    // (dropped mislabel). The landing gap lives in contextData.landingGapSize
    // (CONTEXT_DATA 145); no body code feeds geom.landingGap.
    if (draft.numbers.textAttachmentType !== undefined) geom.textAttachmentType = draft.numbers.textAttachmentType;
    // #256 F1 — leaderLineWeight projected from the (depth-0) body group 171.
    if (draft.strings.leaderLineWeight !== undefined) geom.leaderLineWeight = draft.strings.leaderLineWeight;
    if (draft.numbers.textAttachmentDirection !== undefined) geom.textAttachmentDirection = draft.numbers.textAttachmentDirection;
    if (draft.numbers.textFrameDisplay !== undefined) geom.textFrameDisplay = draft.numbers.textFrameDisplay !== 0;
    // SL-5 F5 (@codex round-2) — project the LEGACY depth-0 body 48/49
    // (draft.numbers.textLineSpacingStyle/Factor, promoted by the compat readers
    // above) onto the flat transport fields so the drawing-model bridge hydrates
    // the model's flat textLineSpacingStyle/Factor (drawingToDxf reads
    // `g.textLineSpacingStyle ?? …` as the fallback after CONTEXT_DATA
    // mTextData). A modern MLEADER whose spacing lives in CONTEXT_DATA MTextData
    // 45/170 never populates these (the readers only fire at depth 0), so its
    // bridge hydration from contextData.mTextData is unchanged.
    if (draft.numbers.textLineSpacingStyle !== undefined) {
      geom.textLineSpacingStyle = draft.numbers.textLineSpacingStyle;
    }
    if (draft.numbers.textLineSpacingFactor !== undefined) {
      geom.textLineSpacingFactor = draft.numbers.textLineSpacingFactor;
    }
    // Skip the leaderLineType projection when the lone 170 was reinterpreted as
    // the legacy content_type above (Codex #268) — in that case 170 was NOT a
    // leader_type, so re-routing it to leaderLineType would double-consume it.
    if (draft.numbers.leaderLineType !== undefined && !legacyContentFrom170) {
      geom.leaderLineType = draft.numbers.leaderLineType;
    }
    if (draft.numbers.propertyOverrideFlags !== undefined) geom.propertyOverrideFlags = draft.numbers.propertyOverrideFlags;
    // gap-C.2 (FIX #1): body 175 → typed textAlignmentType (was propertyOverrideFlags).
    if (draft.numbers.textAlignmentType !== undefined) geom.textAlignmentType = draft.numbers.textAlignmentType;
    if (draft.strings.blockName !== undefined) geom.blockName = draft.strings.blockName;
    // Raw body-344 BLOCK_RECORD handle (resolved → blockName in pass-2).
    if (draft.strings.blockRecordHandle !== undefined) geom.blockRecordHandle = draft.strings.blockRecordHandle;
    if (draft.numbers.blockScale !== undefined) geom.blockScale = draft.numbers.blockScale;
    if (draft.numbers.blockRotation !== undefined) geom.blockRotation = draft.numbers.blockRotation;
    // #432 r9 E2 — depth-0 body block_scale_vector (10/20/30). Captured only when a
    // CONTEXT_DATA was present (the reader gate); a legacy marker-less MLEADER's
    // depth-0 10/20/30 stays a leader vertex (never reaches this point).
    const blockScaleVector = readOptionalPoint(draft.points, "blockScaleVector");
    if (blockScaleVector) geom.blockScaleVector = blockScaleVector;
    // gap-C — body 176 block_connection_type (ezdxf Scope-6).
    if (draft.numbers.blockConnectionType !== undefined) geom.blockConnectionType = draft.numbers.blockConnectionType;

    // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice B) — the typed nested
    // CONTEXT_DATA tree (first context) + verbatim fallback for any
    // unrecognised in-context group (byte-faithfulness, ПРИОРИТЕТ #1).
    if (builderResult.contextData) {
      geom.contextData = builderResult.contextData;
    }
    // Annotative SECONDARY CONTEXT_DATA blocks (one full typed tree per extra
    // scale) — re-emitted by the writer; previously dropped (Codex #268).
    if (builderResult.secondaryContexts.length > 0) {
      geom.secondaryContexts = builderResult.secondaryContexts;
    }
    // Embedded-object-wrapped CONTEXT_DATA (101 "Embedded Object" / 100
    // AcDbMLeaderObjectContextData) — the writer re-wraps on export (Codex #268).
    if (draft.mleaderContextEmbedded) {
      geom.contextDataEmbedded = true;
    }
    if (builderResult.rawContextGroups.length > 0) {
      geom.rawContextGroups = builderResult.rawContextGroups;
    }

    // B1 — trailing ATTDEF/ATTRIB linkage tuples (depth-0 330/177/44/302),
    // collected during the same descent. Empty array when absent.
    if (builderResult.blockAttributeReferences.length > 0) {
      geom.blockAttributeReferences = builderResult.blockAttributeReferences;
    }

    // B2 — one per-scale snapshot per CONTEXT_DATA block, collected during the
    // same descent. Empty when the multileader carries no CONTEXT_DATA.
    if (builderResult.annotativeContexts.length > 0) {
      geom.annotativeContexts = builderResult.annotativeContexts;
    }

    return { ...base, type: "MLEADER", geometry: geom } satisfies DxfMLeaderEntity;
  }

  if (draft.type === "ARRAYRECT") {
    const geom: DxfArrayRectEntity["geometry"] = {
      blockName: draft.strings.blockName ?? "",
      insertionPoint: readPoint(draft.points, "insertion", "ARRAYRECT insertion point"),
      columnCount: draft.numbers.colCount ?? 1,
      rowCount: draft.numbers.rowCount ?? 1
    };
    // Grid spacing 44/45 is presence-aware (Codex W2 #251-B follow-up):
    // absent groups stay `undefined` instead of collapsing to 0, so the
    // bridge resolver can distinguish "no source value" (→ ObjectARX
    // producer default when the params record omits spacing too) from a
    // REAL explicit `44 0` / `45 0` persisted in the source (→ kept).
    if (draft.numbers.colSpacing !== undefined) geom.columnSpacing = draft.numbers.colSpacing;
    if (draft.numbers.rowSpacing !== undefined) geom.rowSpacing = draft.numbers.rowSpacing;
    if (draft.numbers.scaleX !== undefined) geom.scaleX = draft.numbers.scaleX;
    if (draft.numbers.scaleY !== undefined) geom.scaleY = draft.numbers.scaleY;
    if (draft.numbers.scaleZ !== undefined) geom.scaleZ = draft.numbers.scaleZ;
    if (draft.numbers.rotation !== undefined) geom.rotation = draft.numbers.rotation;
    return { ...base, type: "ARRAYRECT", geometry: geom } satisfies DxfArrayRectEntity;
  }

  if (draft.type === "WIPEOUT") {
    // WIPEOUT — flush trailing in-flight vertex if the file ended after
    // group 14 without a matching group 24 (defensive — should not
    // happen in well-formed DXF).
    if (draft.wipeoutCurrentVertex !== null) {
      draft.wipeoutVertices.push({
        x: draft.wipeoutCurrentVertex.x ?? 0,
        y: draft.wipeoutCurrentVertex.y ?? 0,
      });
      draft.wipeoutCurrentVertex = null;
    }
    const wipeoutGeom: DxfWipeoutEntity["geometry"] = {
      // Invert the writer's ±0.5 normalization back to world coordinates.
      vertices: reconstructWipeoutWorldVertices(draft),
      // Frame display flag. The CONFORMANT single-marker form carries NO per-entity
      // frame — the toggle is a DOCUMENT setting (WIPEOUTVARIABLES group 70), so it
      // defaults to shown (WIPEOUTFRAME = 1). The pre-SL-1 legacy dual-marker form,
      // however, smuggled it into a TRAILING entity `280` (captured as
      // `wipeoutFrame`). Recover that value ONLY for the legacy form so opening one
      // of our own older drawings does NOT flip a hidden frame (`280 0`) back ON
      // (the fail-pre bug). Standard form ⇒ default true (unchanged). NOTE: on
      // upgrade-to-save the entity becomes the conformant single-marker form, which
      // has no per-entity frame slot — the SAVE-side round-trip of a non-default
      // frame is the deferred WIPEOUTVARIABLES document-object follow-up (see
      // docs/test-gaps/wipeout-rasterimage-subclass.md).
      frame:
        isLegacyWipeoutForm(draft) && draft.numbers.wipeoutFrame !== undefined
          ? draft.numbers.wipeoutFrame !== 0
          : true,
      // clip_mode (290) — source-preserved (default 0 = outside when omitted).
      clipMode: draft.numbers.wipeoutClipMode === 1 ? 1 : 0,
    };
    return { ...base, type: "WIPEOUT", geometry: wipeoutGeom } satisfies DxfWipeoutEntity;
  }

  // REVCLOUD — stored like LWPOLYLINE with extra xdata
  flushLwVertex(draft);
  const revFlags = draft.numbers.flags ?? 0;
  const revGeom: DxfRevcloudEntity["geometry"] = {
    vertices: draft.lwVertices,
    closed: (revFlags & 1) !== 0,
    // DXF group 70 bit 128 — plinegen. Carried through alongside the closed
    // bit (1) so an imported revcloud (stored as an LWPOLYLINE, 70 = 129 =
    // closed+plinegen) round-trips the flag instead of dropping it on save
    // (@codex #422 P2). Read unconditionally, mirroring the LWPOLYLINE reader;
    // the writer only ORs bit 128 into 70 when it is set (compact 70 otherwise).
    plinegen: (revFlags & 128) !== 0,
    vertexCount: draft.numbers.vertexCount ?? draft.lwVertices.length,
    arcLength: draft.numbers.arcLength ?? 15
  };
  if (draft.numbers.constantWidth !== undefined) revGeom.constantWidth = draft.numbers.constantWidth;
  if (draft.numbers.elevation !== undefined) revGeom.elevation = draft.numbers.elevation;
  // RevcloudProps XData → typed fields. The raw xdata array still
  // round-trips verbatim through the common writer; this projection
  // surfaces the documented sub-values so the bridge can map them onto
  // the typed model. Absent fields stay undefined (model defaults apply).
  const revProps = decodeRevcloudProps(draft.xdata);
  if (revProps.approximateArcLength !== undefined) {
    revGeom.approximateArcLength = revProps.approximateArcLength;
  }
  // @codex #487 P2 — the REVCLOUD "minimum arc length" (typed model
  // `arcChordLength` → geometry `arcLength`) is now carried in the
  // RevcloudProps XData as the SECOND 1040 real (see decodeRevcloudProps). It
  // was previously never emitted, so a min-arc-only edit reverted to the schema
  // default (15) on save+reopen. Project the decoded value onto `arcLength`
  // (overriding the LWPOLYLINE-derived default set at construction) so it
  // round-trips through the bridge (`arcChordLength: g.arcLength`).
  if (revProps.arcChordLength !== undefined) {
    revGeom.arcLength = revProps.arcChordLength;
  }
  if (revProps.revisionCloudStyle !== undefined) {
    revGeom.revisionCloudStyle = revProps.revisionCloudStyle;
  }
  if (revProps.creationMode !== undefined) {
    revGeom.creationMode = revProps.creationMode;
  }
  return { ...base, type: "REVCLOUD", geometry: revGeom } satisfies DxfRevcloudEntity;
}

// RevcloudProps XData appid (AutoCAD 2021+). Exported so the bridge /
// writer encode the same appid string when re-emitting the typed model.
export const REVCLOUD_PROPS_APPID = "RevcloudProps";

// AutoCAD REVCLOUD style enum (1070): 0 = Normal, 1 = Calligraphy.
const REVCLOUD_STYLE_BY_CODE: Record<number, string> = { 0: "Normal", 1: "Calligraphy" };
const REVCLOUD_STYLE_TO_CODE: Record<string, number> = { Normal: 0, Calligraphy: 1 };
// AutoCAD REVCLOUD creation mode enum (1070): 0 = Polygonal, 1 =
// Rectangular, 2 = Object. (Matches the REVCLOUD command sub-options.)
const REVCLOUD_MODE_BY_CODE: Record<number, string> = { 0: "Polygonal", 1: "Rectangular", 2: "Object" };
const REVCLOUD_MODE_TO_CODE: Record<string, number> = { Polygonal: 0, Rectangular: 1, Object: 2 };

type RevcloudPropsDecoded = {
  approximateArcLength?: number;
  arcChordLength?: number;
  revisionCloudStyle?: string;
  creationMode?: string;
};

/**
 * Decode the `RevcloudProps` XData record into typed sub-values.
 *
 * Layout (Autodesk REVCLOUD, 2021+):
 *   1070  <style>          0 = Normal, 1 = Calligraphy
 *   1070  <creation mode>  0 = Polygonal, 1 = Rectangular, 2 = Object
 *   1040  <approx arc>     approximate (maximum) single-segment arc length
 *   1040  <min arc chord>  minimum arc chord length  (@codex #487 P2)
 *
 * The two 1070 integers are taken positionally (first = style, second =
 * mode); the two 1040 reals are taken positionally too (first = approximate
 * arc length, second = minimum arc chord length). Missing codes leave the
 * field undefined so the model schema default applies — so a legacy block
 * carrying only ONE 1040 still decodes it as the approximate arc length and
 * leaves the min-arc chord to default (backward compatible). Exported for the
 * writer encoder + unit tests.
 */
export function decodeRevcloudProps(
  xdata: DxfXDataEntry[],
): RevcloudPropsDecoded {
  const out: RevcloudPropsDecoded = {};
  const entry = xdata.find((x) => x.appName === REVCLOUD_PROPS_APPID);
  if (!entry) return out;
  let intIndex = 0;
  let realIndex = 0;
  for (const g of entry.groups) {
    if (g.code === 1070) {
      const code = Number.parseInt(g.value.trim(), 10);
      if (Number.isInteger(code)) {
        if (intIndex === 0 && REVCLOUD_STYLE_BY_CODE[code] !== undefined) {
          out.revisionCloudStyle = REVCLOUD_STYLE_BY_CODE[code];
        } else if (intIndex === 1 && REVCLOUD_MODE_BY_CODE[code] !== undefined) {
          out.creationMode = REVCLOUD_MODE_BY_CODE[code];
        }
      }
      intIndex += 1;
    } else if (g.code === 1040) {
      const v = Number(g.value.trim());
      if (Number.isFinite(v)) {
        // Positional: first 1040 = approximate arc length, second 1040 =
        // minimum arc chord length. (@codex #487 P2)
        if (realIndex === 0) out.approximateArcLength = v;
        else if (realIndex === 1) out.arcChordLength = v;
      }
      realIndex += 1;
    }
  }
  return out;
}

/**
 * Encode typed REVCLOUD props into a `RevcloudProps` XData entry, matching
 * the layout `decodeRevcloudProps` reads. Returns `null` when every value
 * equals the AutoCAD default (Normal / Rectangular / approx 15 / min-arc 15)
 * so a default revision cloud emits no XData (round-trip-compact). When ANY
 * field is non-default the block emits BOTH 1040 reals (approximate then
 * minimum arc chord) so the positional decode is unambiguous. Exported for
 * the bridge export path + unit tests.
 */
export function encodeRevcloudProps(props: {
  revisionCloudStyle?: string;
  creationMode?: string;
  approximateArcLength?: number;
  // @codex #487 P2 — minimum arc chord length (typed model `arcChordLength`).
  // Previously omitted from the XData, so a min-arc-only edit was lost on save.
  arcChordLength?: number;
}): DxfXDataEntry | null {
  const style = props.revisionCloudStyle ?? "Normal";
  const mode = props.creationMode ?? "Rectangular";
  const arc = props.approximateArcLength ?? 15;
  const chord = props.arcChordLength ?? 15;
  const isDefault =
    style === "Normal" && mode === "Rectangular" && arc === 15 && chord === 15;
  if (isDefault) return null;
  const styleCode = REVCLOUD_STYLE_TO_CODE[style] ?? 0;
  const modeCode = REVCLOUD_MODE_TO_CODE[mode] ?? 1;
  return {
    appName: REVCLOUD_PROPS_APPID,
    groups: [
      { code: 1070, value: String(styleCode) },
      { code: 1070, value: String(modeCode) },
      { code: 1040, value: String(arc) },
      { code: 1040, value: String(chord) },
    ],
  };
}

function isXDataGroupCode(code: number): boolean {
  return code >= 1000 && code <= 1071;
}

export function parseSupportedEntity(
  groups: DxfGroup[],
  stringPool?: StringPool,
): DxfSupportedEntity {
  const header = groups[0];

  if (!header || header.code !== 0 || !isSupportedEntityType(header.value)) {
    throw new Error(`Supported entity parsing requires a recognized entity type header, got "${header?.value}"`);
  }

  // Normalize DXF-file alias → canonical entity type. AutoCAD emits both
  // "MLEADER" and "MULTILEADER" for the same entity; downstream code
  // (createSupportedDraft, ENTITY_GROUP_READERS, isMLeader flag) only
  // knows the canonical "MLEADER" name. See types.ts:ENTITY_TYPE_ALIASES.
  const canonicalType = normalizeEntityType(header.value) as DxfSupportedEntityType;

  const draft = createSupportedDraft(canonicalType, stringPool);
  const entityReaders = ENTITY_GROUP_READERS[canonicalType];

  let currentXDataEntry: DxfXDataEntry | null = null;

  const isMLeader = canonicalType === "MLEADER";
  const isMText = canonicalType === "MTEXT";
  const isAcadTable = canonicalType === "ACAD_TABLE";

  // Group code 101 marks the start of an "Embedded Object Record"
  // subsection (AcDbMTextObjectContextData inside MTEXT, the column
  // metadata block in MTEXT itself, AcDbMLeaderObjectContextData
  // inside MLEADER, AcDbBlockReference attribute payload inside
  // INSERT, etc.). Group codes inside that section share numeric
  // values with the parent entity (40, 41, 42, 43, 44, 71, 72, 73, …)
  // but carry **completely different semantics** — applying the
  // parent entity's reader to them corrupts the parent's properties.
  //
  // Real-world impact: in АБИ1.dxf the MTEXT column gutter group
  // `44 5224.115` lands on top of MTEXT.lineSpacingFactor and the
  // column width `40 5224.115` lands on top of nominalHeight, producing
  // a renderable bbox that extends 27 million world units along the
  // Y-axis. The fit-view path then frames a 27M-unit-tall canvas, the
  // actual drawing geometry collapses to a sub-pixel speck, and the
  // canvas paints empty.
  //
  // The fix matches `ezdxf` and AutoCAD's own DXF semantics: once we
  // observe a `101` group, the remainder of the entity belongs to the
  // embedded object record. We park every group in `extraGroups` for
  // round-trip preservation but DO NOT route them through the parent
  // entity's reader table. XDATA blocks (1001+) take precedence — the
  // existing XDATA branch consumes them before this gate sees them,
  // and the MLEADER CONTEXT_DATA builder is still fed every group (above)
  // so its single-descent decode keeps working.
  let inEmbeddedObject = false;

  // Index-based walk over groups[1..]. Using `groups.slice(1)` here would
  // allocate a throwaway array for every entity parsed — on large files
  // with 5000+ entities that is 5000+ redundant allocations.
  for (let i = 1; i < groups.length; i++) {
    const group = groups[i]!;
    // For MLEADER we need a complete chronological group log so the
    // post-processing helpers (block attribute references, annotative
    // contexts) can scan the full sequence. Reader-consumed codes
    // would otherwise be lost.
    if (isMLeader) {
      // Feed the CONTEXT_DATA nested-tree builder every group, in original
      // order, DURING the walk. It maintains its own frame stack (context →
      // leader/node → leaderLine) routed through the same mleaderContextDepth
      // SSOT, and at finalize yields contextData / leaderRoots /
      // annotativeContexts / blockAttributeReferences in one descent. It only
      // OBSERVES — the existing extraGroups parking + depth-0 body readers are
      // untouched, so the synthesis-from-leaderRoots writer round-trip is
      // unchanged (sub-slice C replaces the writer).
      draft.mleaderBuilder!.feed(group);
      // #432 r9 E1 — latch the AcDbMLeader BODY marker so the depth-0 block-
      // attribute tuple `330` is distinguished from the entity-owner `330`
      // (pre-subclass). Set BEFORE the marker reaches the reader dispatch (where
      // it is recorded in subclassMarkers).
      if (group.code === 100 && group.value.trim() === "AcDbMLeader") {
        draft.mleaderBodyStarted = true;
      }
      // Track CONTEXT_DATA scope so (a) the group-340 reader can tell the
      // text-style handle (inside CONTEXT_DATA / MTextData) apart from the
      // top-level LeaderLineType handle (Codex #243 F1), and (b) the body
      // readers (44, 171, …) only fire at depth 0 (the true AcDbMLeader body),
      // never on a CONTEXT_DATA-scoped value (#256 F2 leak). The shared matcher
      // (mleaderContextDepth) recognises BOTH the real ezdxf/AutoCAD markers
      // (300 open / 301 close context, 302/303 leader, 304/305 leader-line) AND
      // the legacy synthetic markers (302 "LEADER_LINE{" + 303 close). Updated
      // BEFORE the reader dispatch below so a code occurring inside the block is
      // seen as in-context and the markers themselves are correctly classified.
      const nextDepth = advanceMLeaderContextDepth(
        group,
        draft.mleaderContextDepth,
      );
      if (nextDepth !== undefined) {
        // Top-level (depth 0→1) open = a NEW CONTEXT_DATA block starts (only
        // `300 "CONTEXT_DATA{"` can open from depth 0 — the LEADER{ /
        // LEADER_LINE{ tokens are depth-gated in the classifier). Track the
        // 1-based block ordinal so the in-context flat readers below fire for
        // the PRIMARY block only — never for an annotative SECONDARY block
        // (#294 Codex P2: a secondary header 340 eaten by the flat
        // textStyleHandle reader leaked into the primary AND duplicated the
        // secondary raw re-emit).
        if (draft.mleaderContextDepth === 0 && nextDepth === 1) {
          draft.mleaderContextBlockOrdinal += 1;
        }
        draft.mleaderContextDepth = nextDepth;
        draft.mleaderInContextData = nextDepth > 0;
        // This group IS a CONTEXT_DATA nesting marker (300/301/302/303/305, or
        // the dual-use 304 "LEADER_LINE{"). The builder consumed it and the
        // byte-faithful writer (sub-slice C) re-emits the whole CONTEXT_DATA
        // from the typed tree (contextData + leaderRoots + rawContextGroups), so
        // the marker must NOT be parked in extraGroups — doing so would
        // DOUBLE-EMIT the CONTEXT_DATA on a parse → write round-trip. Skip
        // reader dispatch (CRITICAL #258 F1: a `304 "LEADER_LINE{"` marker must
        // not reach the 304 content reader and clobber textContents).
        continue;
      }
      // In-context (depth > 0) non-marker group: the builder OWNS all in-context
      // content (typed contextData + verbatim rawContextGroups) and the writer
      // re-emits it from there. So it must NOT be parked in extraGroups (would
      // double-emit) NOR routed through a flat body reader (would mis-type a
      // context-scoped value — the #256 F2 corruption). The ONLY exception is
      // the small set of flat readers that still feed geom fields downstream
      // (the bridge) consumes: 304 textContents, 340 textStyleHandle, 12/22/32
      // textLocation (MTextData.insert). Fire those, then continue WITHOUT
      // parking. (340 self-gates on mleaderInContextData to capture the
      // in-context text-style handle, not the top-level LeaderLineType 340.)
      //
      // PRIMARY-block gate (#294 Codex P2 — secondary raw 340 duplicated in
      // the primary context): the flat readers project the entity's CURRENT
      // representation, which is the FIRST CONTEXT_DATA block. A per-scale
      // SECONDARY block's 340/304/12/22/32 belongs to that block alone — the
      // builder keeps it typed (`secondaryContexts[].context`) or verbatim
      // (`secondaryContexts[].rawContextGroups`) and the writer re-emits it
      // INSIDE that block. Letting the flat reader eat it clobbered the
      // primary-derived geometry fields (textStyleHandle / textContents /
      // textLocation) and made the primary writer re-emit a foreign value as
      // a primary-block group → leak + duplicate on round-trip.
      if (draft.mleaderInContextData) {
        const reader =
          draft.mleaderContextBlockOrdinal === 1 &&
          MLEADER_INCONTEXT_FLAT_READERS.has(group.code)
            ? entityReaders[group.code]
            : undefined;
        if (reader) reader(draft, group.value);
        continue;
      }
    }

    // Embedded Object Record boundary. Group 101 marks the start of an
    // AcDb<Whatever>ObjectContextData / column metadata block. Latch
    // the flag so subsequent groups bypass the parent entity's reader
    // table (see comment block above the for-loop).
    if (group.code === 101) {
      inEmbeddedObject = true;
      // MLEADER: the embedded object record (`101 "Embedded Object"` /
      // `100 "AcDbMLeaderObjectContextData"`) WRAPS the CONTEXT_DATA. The
      // builder consumes the wrapped 300…301 into the typed tree, and the
      // byte-faithful writer re-emits CONTEXT_DATA + RE-WRAPS it in the 101/100
      // markers when this flag is set. So record the wrapping and DO NOT park
      // the bare 101 in extraGroups (parking re-emits a DANGLING 101 at the
      // entity tail while CONTEXT_DATA moved to top level — Codex #268). The
      // following `100 AcDbMLeaderObjectContextData` is likewise dropped from
      // extraGroups in the inEmbeddedObject branch below.
      if (isMLeader) {
        draft.mleaderContextEmbedded = true;
        continue;
      }
      // Preserve the marker itself for round-trip — same as any
      // unknown group code, so the writer can re-emit it verbatim.
      draft.extraGroups.push(group);
      continue;
    }

    // 102 control-block handling. AutoCAD wraps extension-dictionary
    // and reactor handles inside `102 {ACAD_XDICTIONARY` /
    // `102 {ACAD_REACTORS` markers terminated by a `102 }` close. We
    // capture the typed extensionDictionaryHandle (used by INSERTs of
    // dynamic blocks to point at the AcDbBlockRepresentation DICTIONARY
    // owning the ACDB_BLOCKREPRESENTATION_DATA record). Reactors are
    // round-tripped verbatim through extraGroups for now.
    if (group.code === 102) {
      const open = group.value.trim();
      if (open === "{ACAD_XDICTIONARY") {
        // Walk forward to consume the nested 360 handle and the closing
        // 102 marker. Multiple 360 entries can appear; we keep the
        // first as the typed handle and let the rest fall through to
        // extraGroups (preserves any unusual payload verbatim).
        let j = i + 1;
        while (j < groups.length) {
          const inner = groups[j]!;
          if (inner.code === 102 && inner.value.trim() === "}") {
            break;
          }
          if (
            inner.code === 360 &&
            draft.common.extensionDictionaryHandle === undefined
          ) {
            draft.common.extensionDictionaryHandle = inner.value.trim();
          } else {
            // Unusual payload inside XDICTIONARY block — preserve.
            draft.extraGroups.push(inner);
          }
          j++;
        }
        i = j; // skip past the closing 102 marker
        continue;
      }
      // ACAD_REACTORS or other 102 control blocks — preserve verbatim
      // along with their nested groups until the closing 102.
      draft.extraGroups.push(group);
      let j = i + 1;
      while (j < groups.length) {
        const inner = groups[j]!;
        draft.extraGroups.push(inner);
        if (inner.code === 102 && inner.value.trim() === "}") {
          break;
        }
        j++;
      }
      i = j;
      continue;
    }

    // XDATA handling: code 1001 starts an XDATA block,
    // subsequent codes 1000-1071 belong to it.
    if (group.code === 1001) {
      // Flush previous XDATA block if any
      if (currentXDataEntry) {
        draft.xdata.push(currentXDataEntry);
      }
      // T9 PR-3 — decode the `\U+XXXX`-escaped appid at the MARKER. The 1001
      // group is consumed here (it never reaches the `decodeXdataGroupValue`
      // collector below), so a non-ASCII appid would otherwise stay a literal
      // `\U+…` and consumers that look up entity XDATA by `appName` would miss
      // it after export→reimport.
      currentXDataEntry = {
        appName: decodeDxfUnicode(group.value.trim()),
        groups: [],
      };
      continue;
    }

    if (currentXDataEntry && isXDataGroupCode(group.code)) {
      // T9 PR-3 — decode XDATA 1000/1001/1003 string values at the COLLECTOR so
      // every consumer (e.g. readViewportOverrides frozen-layer match) sees the
      // real characters, not a literal `\U+…`. Re-export re-escapes at the
      // chokepoint → byte-identical. No-op (same ref) for ASCII / non-string codes.
      currentXDataEntry.groups.push(decodeXdataGroupValue(group));
      continue;
    }

    // Non-XDATA code encountered while collecting: flush and resume normal parsing
    if (currentXDataEntry) {
      draft.xdata.push(currentXDataEntry);
      currentXDataEntry = null;
    }

    // While inside an `101 Embedded Object` record, group codes have
    // a different namespace than the parent entity — apply NEITHER
    // entity-specific NOR common readers, just preserve the group for
    // round-trip writeback. This is the gate that prevents the
    // АБИ1.dxf MTEXT column-metadata `44 5224.115` from landing on
    // MTEXT.lineSpacingFactor (see comment block before the for-loop).
    //
    // EXCEPTION (Codex #243 round-4 R4-C): an MLEADER may carry its
    // CONTEXT_DATA INSIDE the embedded object record (after `101
    // AcDbMLeaderObjectContextData`). The text-style STYLE handle is the
    // group-340 of the MTextData block within that CONTEXT_DATA. The
    // `isMLeader` block above already set `mleaderInContextData` from the
    // `300 CONTEXT_DATA{` marker; if we blanket-preserved every group here
    // the group-340 reader would never run and `textStyleHandle` would
    // never be set (bridge import then falls back to "Standard" despite a
    // valid context STYLE handle). So when an MLEADER is inside CONTEXT_DATA
    // scope, route the context-340 through the MLEADER 340 reader (which
    // correctly captures it as the text-style handle, NOT the top-level
    // LeaderLineType handle). All other embedded-object groups still flow
    // verbatim to extraGroups, preserving the MTEXT-column fix.
    if (inEmbeddedObject) {
      // PRIMARY-block gate mirrors the main in-context dispatch above (#294
      // Codex P2): an embedded SECONDARY block's 340 must stay with that block
      // (typed/raw secondary channels), never feed the flat textStyleHandle.
      if (
        isMLeader &&
        draft.mleaderInContextData &&
        draft.mleaderContextBlockOrdinal === 1 &&
        group.code === 340
      ) {
        const reader = entityReaders[340];
        if (reader) {
          reader(draft, group.value);
          continue;
        }
      }
      // MLEADER embedded-CONTEXT_DATA wrapper subclass marker
      // (`100 AcDbMLeaderObjectContextData`, before the `300 CONTEXT_DATA{`).
      // The writer re-wraps from `contextDataEmbedded`, so drop it from
      // extraGroups to avoid a dangling 100 on round-trip (Codex #268). The bare
      // 101 was already suppressed above.
      if (
        isMLeader &&
        draft.mleaderContextEmbedded &&
        group.code === 100 &&
        group.value.trim() === "AcDbMLeaderObjectContextData"
      ) {
        continue;
      }
      // S-MTEXT-COLUMN-HEIGHTS — typed read-only projection of the per-column
      // heights from the `101 AcDbMText` embedded column block. The embedded
      // layout (ezdxf MTextColumns.export_embedded_object) places the column
      // COUNT in group 72, then the repeated per-column 46s LAST. We latch
      // `mtextInEmbeddedColumns` on the embedded 72 and capture every
      // subsequent 46 into `mtextEmbeddedColumnHeights` — WITHOUT consuming the
      // group: it still falls through to extraGroups below, so the writer
      // re-emits the whole embedded block verbatim (single source of truth, no
      // double-emit). A top-level body 46 (scalar defined height, before the
      // `101` marker) is never seen here (this branch only runs inside the
      // embedded object), so the #254 corruption guard stays green.
      if (isMText) {
        if (group.code === 72) {
          draft.mtextInEmbeddedColumns = true;
        } else if (group.code === 46 && draft.mtextInEmbeddedColumns) {
          draft.mtextEmbeddedColumnHeights.push(parseNumberGroup(46, group.value));
        }
      }
      draft.extraGroups.push(group);
      continue;
    }

    // #256 F2 — the in-CONTEXT_DATA depth gate now lives in the `isMLeader`
    // block at the TOP of this loop (the in-context guard): while depth > 0 the
    // builder OWNS every in-context group (typed contextData + verbatim
    // rawContextGroups) and the byte-faithful writer (sub-slice C) re-emits from
    // there, so no in-context code reaches a flat body reader (no mis-typing)
    // and none is parked in extraGroups (no double-emit). Only the depth-0
    // AcDbMLeader body codes reach the readers below.

    // #432 r9 E1 — CONSUME the trailing depth-0 AcDbMLeader block-attribute
    // linkage tuple (`330` ATTDEF handle / `177` index / `44` width / `302` text).
    // The context builder already captured it into geom.blockAttributeReferences
    // (feedBody, above) and the writer re-emits it, so consuming it here prevents
    //   (a) the block-attr `330` from reaching COMMON_GROUP_READERS[330] and
    //       OVERWRITING common.ownerHandle with the ATTDEF handle (owner
    //       corruption), and
    //   (b) `177/44/302` from orphaning into extraGroups (double-emit).
    // The mleaderBodyStarted gate keeps the ENTITY-OWNER `330` (pre-subclass)
    // flowing to ownerHandle; the open/close tracking mirrors the builder so a
    // STRAY `177/44/302` with no open tuple still round-trips verbatim.
    if (isMLeader && draft.mleaderBodyStarted) {
      if (group.code === 330) {
        // A body `330` OPENS a candidate tuple. A previously-pending `330` that
        // never got a field is a BARE opener (@codex r11) → park it verbatim before
        // holding the new one.
        if (draft.mleaderPending330 !== null) {
          draft.extraGroups.push({ code: 330, value: draft.mleaderPending330 });
        }
        draft.mleaderPending330 = group.value;
        draft.mleaderBlockAttrOpen = false;
        continue;
      }
      if (
        draft.mleaderPending330 !== null &&
        (group.code === 177 || group.code === 44 || group.code === 302)
      ) {
        // A field FOLLOWS the pending `330` → it is a REAL tuple: consume the opener
        // (the builder captured it) and this field. `302` closes; `177/44` keep open.
        draft.mleaderPending330 = null;
        draft.mleaderBlockAttrOpen = group.code !== 302;
        continue;
      }
      if (
        draft.mleaderBlockAttrOpen &&
        (group.code === 177 || group.code === 44)
      ) {
        continue;
      }
      if (draft.mleaderBlockAttrOpen && group.code === 302) {
        draft.mleaderBlockAttrOpen = false;
        continue;
      }
      // A non-tuple group arrived while a `330` is pending → that `330` was a BARE
      // opener → park it verbatim, then process THIS group normally (fall through).
      if (draft.mleaderPending330 !== null) {
        draft.extraGroups.push({ code: 330, value: draft.mleaderPending330 });
        draft.mleaderPending330 = null;
      }
    }

    // ACAD_TABLE body gate. Everything after the documented AcDbTable header
    // (…96) is the row/column + per-cell content model, which this change
    // retains VERBATIM rather than decoding (cell editing is a separate
    // feature — see `DxfAcadTableEntity`'s type doc for the exact boundary).
    // Routing it through the reader table would mis-type it: the cell records
    // reuse 90/91/92 with completely different meanings from the header's
    // value-flag / row-count / column-count. Runs after the XDATA branch so a
    // table's XDATA still parses normally.
    if (isAcadTable && feedAcadTableBodyGate(draft, group)) {
      continue;
    }

    // Entity-specific readers take priority over common readers.
    // Some group codes (e.g. 39, 48) have entity-specific semantics
    // that differ from their common meaning.
    const entityReader = entityReaders[group.code];

    if (entityReader) {
      entityReader(draft, group.value);
      continue;
    }

    const commonReader = COMMON_GROUP_READERS[group.code];

    if (commonReader) {
      commonReader(draft, group.value);
      continue;
    }

    draft.extraGroups.push(group);
  }

  // Flush any trailing XDATA block
  if (currentXDataEntry) {
    draft.xdata.push(currentXDataEntry);
  }

  // #432 r11 (@codex) — a body `330` still pending at end-of-body is a BARE opener
  // (no `177/44/302` ever followed) → park it verbatim so it round-trips instead of
  // being fabricated into a block-attribute.
  if (draft.mleaderPending330 !== null) {
    draft.extraGroups.push({ code: 330, value: draft.mleaderPending330 });
    draft.mleaderPending330 = null;
  }

  return finalizeSupportedEntity(draft);
}

export function splitEntities(groups: DxfGroup[]): DxfGroup[][] {
  const entities: DxfGroup[][] = [];
  let current: DxfGroup[] | null = null;

  for (const group of groups) {
    if (group.code === 0) {
      if (current) {
        entities.push(current);
      }

      current = [group];
      continue;
    }

    if (!current) {
      throw new Error("ENTITIES section contains groups before the first entity header");
    }

    current.push(group);
  }

  if (current) {
    entities.push(current);
  }

  return entities;
}

// ── AcadAnnotativeDecomposition (PR-2 — entity-side helper) ───────────
// Implementation extracted to ./entityParser.annotativeBlock.ts to keep
// this file under the 2000-line hard limit. Re-exported here so
// existing import paths (`from "./entityParser"`) keep working.
export { parseAcadAnnotativeDecompositionFromBlock } from "./entityParser.annotativeBlock";

/* ── DIMVAR code→name mapping for DIMSTYLE table entries ── */

